import { Fragment, useEffect, useMemo, useState } from 'react';
import { useRoute } from 'wouter';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Chip,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Tabs,
  Tab,
  Checkbox,
  CheckboxGroup,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  getSinisterSettlementById,
  calculateSinisterLoss,
  getComparableShipTrips,
  ComparableShipTrip,
  createSinisterClaim,
  cancelActiveSinisterClaimsForSettlement,
  getSinisterClaimsBySettlementIds,
  SinisterClaimPresence,
  SinisterClaimSubreport,
} from 'common/src/api/siniestros';
import SinisterEditPopover from '../../../components/settlements/sinister-edit-popover';
import SettlementHistoryDrawer from '../../../components/settlements/settlement-history-drawer';
import { getSettlementHistoryCounts } from '../../../services/historyService';
import {
  Settlement,
  SinisterLossResult,
  SinisterSource,
  SinisterCurrencyMode,
  SinisterPoolMethodology,
} from 'common/src/utils/types';
import { formatMoneyUSD } from '../../../utils/functions';
import { exportTableToExcel } from '../../../utils/export-to-excel';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import { getBrandAssets } from 'common/src/utils/branding';
import {
  Alert,
  BackButton,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
} from '../../../components/ui';

// RMB formatter — used for the vs Mercado source values.
const formatRmb = (value: number, decimals = 0) =>
  `¥${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;

const SHIP_TYPE_KEY: Record<string, string> = {
  M: 'Marítimo',
  A: 'Aéreo',
  T: 'Terrestre',
};

// Market and comparable-pool windows are anchored to the date when the sale
// would have happened without the incident delay.
const MARKET_DAYS_BEFORE = 0;
const POOL_ETA_WINDOW_DAYS = 7;

const addDaysToIsoDate = (value: string, days: number) => {
  const isoDate = value.slice(0, 10);
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split('T')[0];
};

const isoDateToUtcMs = (value: string) => {
  const isoDate = value.slice(0, 10);
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return null;
  return Date.UTC(year, month - 1, day);
};

const formatDateOnly = (value: string) => {
  const isoDate = value.slice(0, 10);
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString();
};

type SourceMeta = {
  title: string;
  short: string;
  description: string;
  bullets: string[];
  icon: string;
  accent: string;
  tint: string;
  ring: string;
};

const SOURCE_META_STATIC: Record<
  SinisterSource,
  Pick<SourceMeta, 'icon' | 'accent' | 'tint' | 'ring'>
> = {
  market: {
    icon: 'tabler:world',
    accent: 'text-blue-600 dark:text-blue-400',
    tint: 'bg-blue-50/60 dark:bg-blue-500/10',
    ring: 'border-blue-200 dark:border-blue-500/30',
  },
  exporter: {
    icon: 'tabler:building-factory-2',
    accent: 'text-emerald-600 dark:text-emerald-400',
    tint: 'bg-emerald-50/60 dark:bg-emerald-500/10',
    ring: 'border-emerald-200 dark:border-emerald-500/30',
  },
  industry: {
    icon: 'tabler:chart-histogram',
    accent: 'text-violet-600 dark:text-violet-400',
    tint: 'bg-violet-50/60 dark:bg-violet-500/10',
    ring: 'border-violet-200 dark:border-violet-500/30',
  },
};

export default function SiniestroDetailPage() {
  const { t } = useTranslation('common');

  const SOURCE_META: Record<SinisterSource, SourceMeta> = useMemo(
    () => ({
      market: {
        ...SOURCE_META_STATIC.market,
        title: t('src.market.title'),
        short: t('src.market.short'),
        description: t('src.market.description'),
        bullets: [t('src.market.bullet1')],
      },
      exporter: {
        ...SOURCE_META_STATIC.exporter,
        title: t('src.exporter.title'),
        short: t('src.exporter.short'),
        description: t('src.exporter.description'),
        bullets: [t('src.exporter.bullet1'), t('src.exporter.bullet2')],
      },
      industry: {
        ...SOURCE_META_STATIC.industry,
        title: t('src.industry.title'),
        short: t('src.industry.short'),
        description: t('src.industry.description'),
        bullets: [t('src.industry.bullet1'), t('src.industry.bullet2')],
      },
    }),
    [t]
  );

  // Localized ship type label helper.
  const shipTypeLabel = (type: string | null | undefined): string => {
    if (!type) return '-';
    const key = SHIP_TYPE_KEY[type];
    return key ? t(key) : type;
  };

  const [, params] = useRoute<{ id: string }>('/settlements/siniestros/:id');
  const id = params?.id ? Number(params.id) : null;

  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [results, setResults] = useState<SinisterLossResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);

  // Params (Exporter / Industry — common)
  const [currencyMode, setCurrencyMode] = useState<SinisterCurrencyMode>('FOB');
  const [poolMethodology, setPoolMethodology] =
    useState<SinisterPoolMethodology>('by_exporter');

  // Params (Mercado — inline in the bluish card)
  const [marketRefDate, setMarketRefDate] = useState<string>('');
  const [marketRegion, setMarketRegion] = useState<'SH' | 'GZ' | ''>('');
  // Display currency for Mercado values (RMB native, optional USD conversion)
  const [marketDisplayCurrency, setMarketDisplayCurrency] = useState<
    'RMB' | 'USD'
  >('USD');
  const [marketExchangeRate, setMarketExchangeRate] = useState<string>('');

  // Ship pool
  const [candidates, setCandidates] = useState<ComparableShipTrip[]>([]);
  const [selectedTripIds, setSelectedTripIds] = useState<Set<number>>(
    new Set()
  );

  // PDF export options
  const [pdfSources, setPdfSources] = useState<SinisterSource[]>([
    'market',
    'exporter',
    'industry',
  ]);
  const [pdfIncludeDetail, setPdfIncludeDetail] = useState(false);
  const [pdfLanguage, setPdfLanguage] = useState<'es' | 'en'>('en');

  // Save-as-claim flow
  const [savedClaims, setSavedClaims] = useState<SinisterClaimPresence | null>(
    null
  );
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveSelectedSources, setSaveSelectedSources] = useState<
    SinisterSource[]
  >(['market', 'exporter', 'industry']);
  const [saveNotes, setSaveNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyCount, setHistoryCount] = useState(0);

  // Recompute the suggested market reference date whenever the settlement's
  // date_sold or delay days change (e.g. user edits delay via the popover).
  useEffect(() => {
    if (!settlement?.date_sold) return;
    const refDate = addDaysToIsoDate(
      settlement.date_sold,
      -(settlement.sinister_delay_days || 0)
    );
    if (refDate) setMarketRefDate(refDate);
  }, [settlement?.date_sold, settlement?.sinister_delay_days]);

  // Helper: compute the ETA window the candidate pool will be restricted to.
  const poolWindow = useMemo(() => {
    if (!settlement) return null;
    const delayDays = settlement.sinister_delay_days || 0;
    const anchors: number[] = [];
    if (settlement.date_sold) {
      const delayedDateSold = addDaysToIsoDate(settlement.date_sold, -delayDays);
      const dateSoldMs = delayedDateSold
        ? isoDateToUtcMs(delayedDateSold)
        : null;
      if (dateSoldMs != null) anchors.push(dateSoldMs);
    }
    if (settlement.ship_trip?.eta) {
      const delayedEta = addDaysToIsoDate(settlement.ship_trip.eta, -delayDays);
      const etaMs = delayedEta ? isoDateToUtcMs(delayedEta) : null;
      if (etaMs != null) anchors.push(etaMs);
    }
    if (anchors.length === 0) return null;
    const minMs = Math.min(...anchors);
    const maxMs = Math.max(...anchors);
    const start = new Date(minMs);
    start.setUTCDate(start.getUTCDate() - POOL_ETA_WINDOW_DAYS);
    const end = new Date(maxMs);
    end.setUTCDate(end.getUTCDate() + POOL_ETA_WINDOW_DAYS);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  }, [settlement]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const settlementData = await getSinisterSettlementById(id);
        setSettlement(settlementData);
        if (settlementData) {
          // Default currency toggle from original incoterm if available
          if (settlementData.original_incoterm === 'CIF') {
            setCurrencyMode('CIF');
          }
          // Mercado defaults
          if (settlementData.date_sold) {
            const refDate = addDaysToIsoDate(
              settlementData.date_sold,
              -(settlementData.sinister_delay_days || 0)
            );
            if (refDate) setMarketRefDate(refDate);
          }
          // If the settlement market matches SH/GZ, suggest it; otherwise leave empty.
          const mid = (settlementData.market_id || '').toString();
          if (mid === 'CNSHA') setMarketRegion('SH');
          else if (mid === 'CNCAN') setMarketRegion('GZ');
          else setMarketRegion('');
          // Default exchange rate for Mercado USD display
          if (settlementData.exchange_rate) {
            setMarketExchangeRate(String(settlementData.exchange_rate));
          }
          // Load existing sinister claims for this settlement (badge in UI)
          getSinisterClaimsBySettlementIds([settlementData.id]).then((map) => {
            setSavedClaims(map.get(settlementData.id) || null);
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!settlement?.id) {
      setHistoryCount(0);
      return;
    }

    let cancelled = false;
    getSettlementHistoryCounts([settlement.id]).then((countsMap) => {
      if (!cancelled) setHistoryCount(countsMap.get(settlement.id) || 0);
    });

    return () => {
      cancelled = true;
    };
  }, [settlement?.id]);

  useEffect(() => {
    if (!settlement) return;
    let cancelled = false;
    const loadComparableShips = async () => {
      const etaMin = poolWindow?.start;
      const etaMax = poolWindow?.end;
      const ships = await getComparableShipTrips({
        market_id: settlement.market_id,
        pod_market_id: settlement.ship_trip?.market_id ?? null,
        species_id: settlement.species_id,
        season: settlement.season,
        ship_type: settlement.ship_trip?.ship?.type,
        eta_min: etaMin,
        eta_max: etaMax,
      });
      if (cancelled) return;
      // The sinister's own ship_trip is a valid comparable too — non-sinister
      // settlements on the same vessel are filtered server-side by the edge function.
      setCandidates(ships);
      const availableTripIds = new Set(ships.map((ship) => ship.id));
      setSelectedTripIds(
        (prev) =>
          new Set([...prev].filter((tripId) => availableTripIds.has(tripId)))
      );
    };
    loadComparableShips();
    return () => {
      cancelled = true;
    };
  }, [
    settlement?.id,
    settlement?.market_id,
    settlement?.ship_trip?.market_id,
    settlement?.species_id,
    settlement?.season,
    settlement?.ship_trip?.ship?.type,
    settlement?.date_sold,
    settlement?.ship_trip?.eta,
    settlement?.sinister_delay_days,
    poolWindow?.start,
    poolWindow?.end,
  ]);

  const toggleTrip = (tripId: number) => {
    setSelectedTripIds((prev) => {
      const next = new Set(prev);
      if (next.has(tripId)) next.delete(tripId);
      else next.add(tripId);
      return next;
    });
  };

  const handleCalculate = async () => {
    if (!id) return;
    setIsCalculating(true);
    try {
      const fresh = await calculateSinisterLoss({
        settlement_id: id,
        market_days_before: MARKET_DAYS_BEFORE,
        market_compare_region: marketRegion || null,
        market_reference_date: marketRefDate || null,
        currency_mode: currencyMode,
        comparable_ship_trip_ids: Array.from(selectedTripIds),
        pool_methodology: poolMethodology,
      });
      setResults(fresh);
      toast.success(t('Perjuicio calculado'));
    } catch (e: any) {
      toast.error(e?.message || t('Error al calcular el perjuicio'));
    } finally {
      setIsCalculating(false);
    }
  };

  const bySource = useMemo(() => {
    const map: Record<SinisterSource, SinisterLossResult[]> = {
      market: [],
      exporter: [],
      industry: [],
    };
    for (const r of results) {
      map[r.source]?.push(r);
    }
    return map;
  }, [results]);

  // Summary by (variety, caliber): one row per group with actual + benchmark + loss
  // from each source. Mercado values in RMB, Exporter/Industry in USD.
  const summary = useMemo(() => {
    type Row = {
      variety: string;
      caliber: string;
      kilos: number;
      // Mercado side (RMB)
      actualMarketRmb: number | null;
      benchmarkMarket: number | null;
      lossByMarket: number | null;
      // Shared own value for Exporter / Industry (USD/kg, FOB or CIF)
      actualUsdPerKg: number | null;
      // Exporter (USD)
      benchmarkExporter: number | null;
      lossByExporter: number | null;
      // Industry (USD)
      benchmarkIndustry: number | null;
      lossByIndustry: number | null;
    };
    const map = new Map<string, Row>();
    for (const r of results) {
      const key = `${(r.variety || '').toUpperCase()}||${r.caliber || ''}`;
      let row = map.get(key);
      if (!row) {
        row = {
          variety: r.variety || '-',
          caliber: r.caliber || '-',
          kilos: Number(r.kilos || 0),
          actualMarketRmb: null,
          benchmarkMarket: null,
          lossByMarket: null,
          actualUsdPerKg: null,
          benchmarkExporter: null,
          lossByExporter: null,
          benchmarkIndustry: null,
          lossByIndustry: null,
        };
        map.set(key, row);
      }
      const benchmark =
        r.benchmark_price_per_kg != null
          ? Number(r.benchmark_price_per_kg)
          : null;
      const actual =
        r.actual_price_per_kg != null ? Number(r.actual_price_per_kg) : null;
      const loss = r.loss_usd != null ? Number(r.loss_usd) : null;
      if (r.source === 'market') {
        row.actualMarketRmb = actual;
        row.benchmarkMarket = benchmark;
        row.lossByMarket = loss;
      }
      if (r.source === 'exporter') {
        row.actualUsdPerKg = actual;
        row.benchmarkExporter = benchmark;
        row.lossByExporter = loss;
      }
      if (r.source === 'industry') {
        // Same actual as exporter — keep first value seen
        if (row.actualUsdPerKg == null) row.actualUsdPerKg = actual;
        row.benchmarkIndustry = benchmark;
        row.lossByIndustry = loss;
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.variety !== b.variety) return a.variety.localeCompare(b.variety);
      return a.caliber.localeCompare(b.caliber);
    });
  }, [results]);

  // Convert a Mercado RMB value to USD (or pass-through) based on the user
  // selection in the Mercado config card.
  const convertMarketValue = (rmbValue: number | null): number | null => {
    if (rmbValue == null) return null;
    if (marketDisplayCurrency === 'RMB') return rmbValue;
    const rate = Number(marketExchangeRate) || 0;
    if (rate <= 0) return null;
    return rmbValue / rate;
  };

  // Format a P&L value with explicit sign + currency.
  // Negative = loss (rendered with leading "-" by the formatter).
  // Positive = gain (we prepend "+" so the sign is unambiguous).
  // Zero or null are returned plain.
  const formatPnl = (
    value: number | null,
    currency: 'RMB' | 'USD',
    decimals = 0
  ): string => {
    if (value == null) return '-';
    const formatted =
      currency === 'RMB'
        ? formatRmb(value, decimals)
        : formatMoneyUSD(value, { decimals } as any);
    if (value > 0) return `+${formatted}`;
    return formatted;
  };

  const summaryTotals = useMemo(() => {
    return summary.reduce(
      (acc, r) => ({
        kilos: acc.kilos + r.kilos,
        lossMarket: acc.lossMarket + (r.lossByMarket || 0),
        lossExporter: acc.lossExporter + (r.lossByExporter || 0),
        lossIndustry: acc.lossIndustry + (r.lossByIndustry || 0),
      }),
      {
        kilos: 0,
        lossMarket: 0,
        lossExporter: 0,
        lossIndustry: 0,
      }
    );
  }, [summary]);

  // Per-line breakdown: every settlement_detail with its own actual values and
  // the per-line P&L derived from the (variety, caliber) group benchmark.
  // The sum of per-line P&L within a group equals the group's P&L from the
  // resumen (math identity: see footer note).
  interface DetailLine {
    detail_id: number;
    variety: string;
    caliber: string;
    color: string;
    format: string;
    label: string;
    kilos: number;
    actualMarketRmb: number; // local_total_price / kilos
    actualUsdPerKg: number; // (usd_total_fob [+ usd_freight if CIF]) / kilos
    benchmarkMarket: number | null;
    lossMarketRmb: number | null;
    benchmarkExporter: number | null;
    lossExporterUsd: number | null;
    benchmarkIndustry: number | null;
    lossIndustryUsd: number | null;
  }

  const detailLines = useMemo<DetailLine[]>(() => {
    if (!settlement || results.length === 0) return [];
    // Index summary rows by group key for fast lookup
    const summaryByKey = new Map<string, (typeof summary)[number]>();
    for (const s of summary) {
      const key = `${s.variety.toUpperCase()}||${s.caliber}`;
      summaryByKey.set(key, s);
    }
    const lines: DetailLine[] = [];
    for (const d of settlement.settlement_details || []) {
      const kilos = Number(d.kilos) || 0;
      if (kilos <= 0) continue;
      const fob = Number(d.usd_total_fob) || 0;
      const freight = Number(d.usd_freight) || 0;
      const localTotal = Number(d.local_total_price) || 0;
      const actualMarketRmb = localTotal / kilos;
      const actualUsdPerKg =
        (currencyMode === 'CIF' ? fob + freight : fob) / kilos;
      const key = `${(d.variety || '').toUpperCase()}||${d.caliber || '-'}`;
      const groupSummary = summaryByKey.get(key);

      const bM = groupSummary?.benchmarkMarket ?? null;
      const bE = groupSummary?.benchmarkExporter ?? null;
      const bI = groupSummary?.benchmarkIndustry ?? null;

      lines.push({
        detail_id: d.id,
        variety: d.variety || '-',
        caliber: d.caliber || '-',
        color: d.color || '-',
        format: d.format || '-',
        label: d.label || '-',
        kilos,
        actualMarketRmb,
        actualUsdPerKg,
        benchmarkMarket: bM,
        lossMarketRmb: bM != null ? (actualMarketRmb - bM) * kilos : null,
        benchmarkExporter: bE,
        lossExporterUsd: bE != null ? (actualUsdPerKg - bE) * kilos : null,
        benchmarkIndustry: bI,
        lossIndustryUsd: bI != null ? (actualUsdPerKg - bI) * kilos : null,
      });
    }
    return lines.sort((a, b) => {
      if (a.variety !== b.variety) return a.variety.localeCompare(b.variety);
      if (a.caliber !== b.caliber) return a.caliber.localeCompare(b.caliber);
      return a.color.localeCompare(b.color);
    });
  }, [settlement, results, summary, currencyMode]);

  // Export the per-line detail breakdown as an .xlsx file.
  const handleExportDetailExcel = () => {
    if (!settlement || detailLines.length === 0) {
      toast.error(t('No hay líneas que exportar'));
      return;
    }
    const headers = [
      t('Variedad'),
      t('Calibre'),
      t('Color'),
      t('Formato'),
      t('Etiqueta'),
      t('Kilos'),
      `${t('Venta Propio')} (${marketDisplayCurrency})`,
      `${t('Comp.')} ${t('Mercado')} (${marketDisplayCurrency})`,
      `${t('Perjuicio')} ${t('Mercado')} (${marketDisplayCurrency})`,
      `${t('{{mode}} Propio', { mode: currencyMode })} (USD)`,
      `${t('Comp.')} ${t('Exportadora')} ${currencyMode} (USD)`,
      `${t('Perjuicio')} ${t('Exportadora')} (USD)`,
      `${t('Comp.')} ${t('Industria')} ${currencyMode} (USD)`,
      `${t('Perjuicio')} ${t('Industria')} (USD)`,
    ];
    const rows = detailLines.map((d) => {
      const actMarket = convertMarketValue(d.actualMarketRmb);
      const benchMarket = convertMarketValue(d.benchmarkMarket);
      const lossMarket = convertMarketValue(d.lossMarketRmb);
      return [
        d.variety,
        d.caliber,
        d.color,
        d.format,
        d.label,
        d.kilos,
        actMarket != null ? Number(actMarket.toFixed(4)) : null,
        benchMarket != null ? Number(benchMarket.toFixed(4)) : null,
        lossMarket != null ? Number(lossMarket.toFixed(2)) : null,
        Number(d.actualUsdPerKg.toFixed(4)),
        d.benchmarkExporter != null
          ? Number(d.benchmarkExporter.toFixed(4))
          : null,
        d.lossExporterUsd != null ? Number(d.lossExporterUsd.toFixed(2)) : null,
        d.benchmarkIndustry != null
          ? Number(d.benchmarkIndustry.toFixed(4))
          : null,
        d.lossIndustryUsd != null ? Number(d.lossIndustryUsd.toFixed(2)) : null,
      ];
    });
    exportTableToExcel({
      fileName: `siniestro-${
        settlement.container_number || settlement.id
      }-detalle`,
      sheetName: t('Desglose por línea de detalle'),
      headers,
      rows,
    });
  };

  // Build the payload to persist a single subreport as a claim.
  const buildClaimInput = (
    source: SinisterSource
  ): {
    subreport: SinisterClaimSubreport;
    payload: Parameters<typeof createSinisterClaim>[0];
  } | null => {
    if (!settlement) return null;

    const refDateLabel = marketRefDate
      ? new Date(marketRefDate).toLocaleDateString()
      : '-';
    const containerLabel =
      settlement.container_number ||
      settlement.reference_number ||
      `#${settlement.id}`;

    if (source === 'market') {
      // Sign convention (matches comparative-clients): claim amount stored as
      // POSITIVE = the amount being claimed back. Internal P&L is negative
      // when there's a loss, so we negate.
      const claimAmountRmb = -summaryTotals.lossMarket;
      const details = detailLines
        .filter((d) => d.benchmarkMarket != null)
        .map((d) => ({
          settlement_detail_id: d.detail_id,
          comparable_per_kg: d.benchmarkMarket as number,
          comparable_kilos: d.kilos,
          own_value_per_kg: d.actualMarketRmb,
        }));
      return {
        subreport: 'sinister_market',
        payload: {
          subreport: 'sinister_market',
          exporter_id: Number(settlement.exporter_id),
          receiver: settlement.receiver,
          season: settlement.season,
          total_amount: claimAmountRmb,
          currency: 'RMB',
          description: `Siniestro ${containerLabel} — vs Mercado (${marketRegion || '?'}, ${refDateLabel})`,
          notes: saveNotes || null,
          comparable_ship_trip_ids: [],
          market_filter: marketRegion ? [marketRegion] : [],
          incoterm: null,
          comparison_target: 'median',
          pool_methodology: null,
          market_reference_date: marketRefDate || null,
          exchange_rate_used:
            marketDisplayCurrency === 'USD'
              ? Number(marketExchangeRate) || null
              : null,
          details,
        },
      };
    }

    if (source === 'exporter') {
      const details = detailLines
        .filter((d) => d.benchmarkExporter != null)
        .map((d) => ({
          settlement_detail_id: d.detail_id,
          comparable_per_kg: d.benchmarkExporter as number,
          comparable_kilos: d.kilos,
          own_value_per_kg: d.actualUsdPerKg,
        }));
      return {
        subreport: 'sinister_exporter',
        payload: {
          subreport: 'sinister_exporter',
          exporter_id: Number(settlement.exporter_id),
          receiver: settlement.receiver,
          season: settlement.season,
          total_amount: -summaryTotals.lossExporter,
          currency: 'USD',
          description: `Siniestro ${containerLabel} — vs Exportadora (${currencyMode})`,
          notes: saveNotes || null,
          comparable_ship_trip_ids: Array.from(selectedTripIds),
          market_filter: [],
          incoterm: currencyMode,
          comparison_target: 'average',
          pool_methodology: null,
          market_reference_date: null,
          exchange_rate_used: null,
          details,
        },
      };
    }

    // industry
    const details = detailLines
      .filter((d) => d.benchmarkIndustry != null)
      .map((d) => ({
        settlement_detail_id: d.detail_id,
        comparable_per_kg: d.benchmarkIndustry as number,
        comparable_kilos: d.kilos,
        own_value_per_kg: d.actualUsdPerKg,
      }));
    return {
      subreport: 'sinister_industry',
      payload: {
        subreport: 'sinister_industry',
        exporter_id: Number(settlement.exporter_id),
        receiver: settlement.receiver,
        season: settlement.season,
        total_amount: -summaryTotals.lossIndustry,
        currency: 'USD',
        description: `Siniestro ${containerLabel} — vs Industria (${currencyMode}, ${poolMethodology})`,
        notes: saveNotes || null,
        comparable_ship_trip_ids: Array.from(selectedTripIds),
        market_filter: [],
        incoterm: currencyMode,
        comparison_target: 'median',
        pool_methodology: poolMethodology,
        market_reference_date: null,
        exchange_rate_used: null,
        details,
      },
    };
  };

  const handleSaveClaims = async () => {
    if (!settlement || saveSelectedSources.length === 0) return;
    setIsSaving(true);
    try {
      // Cancel any active draft/submitted sinister claims for this settlement
      // first — re-saving overwrites stale drafts but never touches accepted ones.
      await cancelActiveSinisterClaimsForSettlement(settlement.id);

      const newIds: number[] = [];
      for (const src of saveSelectedSources) {
        // Defensive: skip sources that don't qualify (positive P&L or no data)
        if (!sourceCanBeSaved(src).allowed) continue;
        const built = buildClaimInput(src);
        if (!built || built.payload.details.length === 0) continue;
        const { claim_id } = await createSinisterClaim(built.payload);
        newIds.push(claim_id);
      }
      toast.success(
        newIds.length === 1
          ? t('Compensación guardada ({{count}} claim).', {
              count: newIds.length,
            })
          : t('Compensación guardada ({{count}} claims).', {
              count: newIds.length,
            })
      );
      // Refresh presence map
      const fresh = await getSinisterClaimsBySettlementIds([settlement.id]);
      setSavedClaims(fresh.get(settlement.id) || null);
      setSaveModalOpen(false);
      setSaveNotes('');
    } catch (e: any) {
      console.error('Error saving sinister claims:', e);
      toast.error(e?.message || t('Error al guardar la compensación'));
    } finally {
      setIsSaving(false);
    }
  };

  // Returns whether a sinister subreport qualifies to be saved as a claim.
  // Convention (matches comparative-clients): only "poor results" (a loss) are
  // saveable. With our P&L sign (positive = gain, negative = loss), this means
  // total < 0. Also requires at least one detail with a benchmark.
  // Additionally: if there's already a DISTRIBUTED claim for this source, we
  // block re-save until the user undistributes it (revenues are applied).
  const sourceCanBeSaved = (
    source: SinisterSource
  ): { allowed: boolean; reason?: string } => {
    const total =
      source === 'market'
        ? summaryTotals.lossMarket
        : source === 'exporter'
          ? summaryTotals.lossExporter
          : summaryTotals.lossIndustry;
    const detailsCount = detailLines.filter((d) =>
      source === 'market'
        ? d.benchmarkMarket != null
        : source === 'exporter'
          ? d.benchmarkExporter != null
          : d.benchmarkIndustry != null
    ).length;
    if (detailsCount === 0)
      return { allowed: false, reason: t('Sin benchmark / sin datos.') };
    if (total >= 0)
      return {
        allowed: false,
        reason: t('Resultado positivo: no hay perjuicio que reclamar.'),
      };
    const slot: keyof SinisterClaimPresence =
      source === 'market'
        ? 'market'
        : source === 'exporter'
          ? 'exporter'
          : 'industry';
    const existing = savedClaims?.[slot];
    if (existing?.status === 'distributed') {
      return {
        allowed: false,
        reason: t(
          'Debes eliminar la distribución de esta compensación (en Compensaciones) antes de guardarla nuevamente.'
        ),
      };
    }
    return { allowed: true };
  };

  const eligibleSavableSources = (
    ['market', 'exporter', 'industry'] as SinisterSource[]
  ).filter((s) => sourceCanBeSaved(s).allowed);

  const loadImageAsBase64 = (src: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        try {
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  const handleExportPdf = async () => {
    if (!settlement) return;
    if (pdfSources.length === 0) {
      toast.error(t('Selecciona al menos un análisis'));
      return;
    }

    // Localized strings for the PDF body. Decoupled from the UI language so
    // the user can pick es/en independently when exporting.
    const lang = pdfLanguage;
    const isES = lang === 'es';
    const S = {
      certificate: isES ? 'C E R T I F I C A D O' : 'C E R T I F I C A T E',
      subtitle: isES ? 'Reporte de Pérdida por Siniestro' : 'Incident Loss Report',
      confidential: isES ? 'Confidencial' : 'Confidential',
      page: isES ? 'Página' : 'Page',
      container: isES ? 'CONTENEDOR' : 'CONTAINER',
      receiver: isES ? 'CLIENTE' : 'RECEIVER',
      saleMarket: isES ? 'MERCADO DE VENTA' : 'SALE MARKET',
      vessel: isES ? 'NAVE' : 'VESSEL',
      delay: isES ? 'RETRASO' : 'DELAY',
      caseNumber: isES ? 'NÚMERO DE CASO' : 'CASE NUMBER',
      saleDate: isES ? 'FECHA DE VENTA' : 'SALE DATE',
      season: isES ? 'TEMPORADA' : 'SEASON',
      comparableMarket: isES ? 'MERCADO COMPARABLE' : 'COMPARABLE MARKET',
      incoterm: isES ? 'INCOTERM' : 'INCOTERM',
      industryWeighting: isES ? 'PONDERACIÓN INDUSTRIA' : 'INDUSTRY WEIGHTING',
      byVolume: isES ? 'Por Volumen' : 'By Volume',
      byExporter: isES ? 'Por Exportadora' : 'By Exporter',
      fxLabel: 'FX RMB > USD',
      pnlVsMarket: isES ? 'P&L VS MERCADO' : 'P&L VS MARKET',
      pnlVsExporter: isES ? 'P&L VS EXPORTADORA (USD)' : 'P&L VS EXPORTER (USD)',
      pnlVsIndustry: isES ? 'P&L VS INDUSTRIA (USD)' : 'P&L VS INDUSTRY (USD)',
      poolTitle: isES ? 'Pool de Naves Comparables' : 'Comparable Vessels Pool',
      tableVessel: isES ? 'Nave' : 'Vessel',
      tableType: isES ? 'Tipo' : 'Type',
      tableVoyage: isES ? 'Voyage' : 'Voyage',
      tablePod: isES ? 'POD' : 'POD',
      tableEta: 'ETA',
      summaryTitle: isES
        ? 'Resumen de Pérdida por Variedad y Calibre'
        : 'Loss Summary by Variety & Size',
      headVariety: isES ? 'Variedad' : 'Variety',
      headSize: isES ? 'Calibre' : 'Size',
      headKilos: 'Kilos',
      headColor: isES ? 'Color' : 'Color',
      saleOwn: isES ? 'Venta Propio' : 'Sale Own',
      ownSuffix: '/kg',
      saleVsMarket: isES ? 'Venta vs Mercado' : 'Sale vs Market',
      vsExporter: isES ? 'vs Exportadora' : 'vs Exporter',
      vsIndustry: isES ? 'vs Industria' : 'vs Industry',
      headComp: isES ? 'Comp.' : 'Comp.',
      headPnl: 'P&L',
      total: isES ? 'TOTAL' : 'TOTAL',
      perLineTitle: isES
        ? 'Desglose por Línea de Detalle'
        : 'Per-line detail breakdown',
      methodologyTitle: isES ? 'Notas Metodológicas' : 'Methodology Notes',
      methodPnlMarket: isES ? 'P&L vs Mercado' : 'P&L vs Market',
      methodPnlExporter: isES ? 'P&L vs Exportadora' : 'P&L vs Exporter',
      methodPnlIndustry: isES ? 'P&L vs Industria' : 'P&L vs Industry',
      noDelay: isES ? 'Sin retraso' : 'No delay',
      days: isES ? 'días' : 'days',
      shipMaritime: isES ? 'Marítimo' : 'Maritime',
      shipAir: isES ? 'Aéreo' : 'Air',
      shipGround: isES ? 'Terrestre' : 'Ground',
    };
    const localShipTypeLabel = (type: string | null | undefined): string => {
      if (!type) return '-';
      if (type === 'M') return S.shipMaritime;
      if (type === 'A') return S.shipAir;
      if (type === 'T') return S.shipGround;
      return type;
    };
    const dateLocale = isES ? 'es-CL' : 'en-US';

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 18;
      const contentWidth = pageWidth - margin * 2;
      const centerX = pageWidth / 2;

      // Brand palette (matches comparative-clients)
      const cherry = { r: 190, g: 18, b: 60 };
      const charcoal = { r: 24, g: 24, b: 27 };
      const slate = { r: 71, g: 85, b: 105 };
      const muted = { r: 148, g: 163, b: 184 };
      const faintBg = { r: 248, g: 250, b: 252 };
      const borderC = { r: 226, g: 232, b: 240 };
      const green = { r: 22, g: 163, b: 74 };
      const red = { r: 220, g: 38, b: 38 };
      const logoAspect = 1575 / 245;

      const whiteLogoBase64 = await loadImageAsBase64(
        getBrandAssets().fullLogoDark
      );

      // jsPDF's helvetica core fonts only support WinAnsi (Latin-1). Special
      // chars like em-dash, minus sign, smart quotes render with width 0,
      // which breaks splitTextToSize and produces truncated words mid-character.
      const sanitizeForPdf = (s: string): string =>
        s
          .replace(/[—–]/g, '-')
          .replace(/−/g, '-')
          .replace(/[""]/g, '"')
          .replace(/['']/g, "'")
          .replace(/…/g, '...')
          .replace(/[^\x00-\xff]/g, '?');

      const today = new Date();
      const dateStr = today.toLocaleDateString(dateLocale, {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      });

      const includeMarket = pdfSources.includes('market');
      const includeExporter = pdfSources.includes('exporter');
      const includeIndustry = pdfSources.includes('industry');

      // Footer drawer (called at the end)
      const drawFooter = () => {
        const totalPages = doc.getNumberOfPages();
        for (let p = 1; p <= totalPages; p++) {
          doc.setPage(p);
          const footH = 10;
          doc.setFillColor(charcoal.r, charcoal.g, charcoal.b);
          doc.rect(0, pageHeight - footH, pageWidth, footH, 'F');
          doc.setFillColor(cherry.r, cherry.g, cherry.b);
          doc.rect(0, pageHeight - footH, pageWidth, 1, 'F');
          if (whiteLogoBase64) {
            const fH = 3.5;
            doc.addImage(
              whiteLogoBase64,
              'PNG',
              margin,
              pageHeight - footH / 2 - fH / 2,
              fH * logoAspect,
              fH
            );
          }
          doc.setFontSize(6);
          doc.setTextColor(160, 160, 160);
          doc.setFont('helvetica', 'normal');
          doc.text(
            `${S.confidential}  ·  ${dateStr}  ·  ${S.page} ${p}/${totalPages}`,
            pageWidth - margin,
            pageHeight - footH / 2 + 1.5,
            { align: 'right' }
          );
        }
      };

      // ═══ HEADER ═══
      const headerH = 26;
      doc.setFillColor(charcoal.r, charcoal.g, charcoal.b);
      doc.rect(0, 0, pageWidth, headerH, 'F');
      doc.setFillColor(cherry.r, cherry.g, cherry.b);
      doc.rect(0, headerH, pageWidth, 1.2, 'F');
      if (whiteLogoBase64) {
        const lH = 6.5;
        doc.addImage(
          whiteLogoBase64,
          'PNG',
          margin,
          (headerH - lH) / 2,
          lH * logoAspect,
          lH
        );
      }
      const certNum = `${isES ? 'N°' : 'No.'} ${settlement.id.toString().padStart(8, '0')}`;
      doc.setFontSize(7);
      doc.setTextColor(130, 130, 130);
      doc.setFont('helvetica', 'normal');
      doc.text(certNum, pageWidth - margin, headerH / 2 + 1, {
        align: 'right',
      });

      // ═══ TITLE ═══
      let y = headerH + 13;
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(charcoal.r, charcoal.g, charcoal.b);
      doc.text(S.certificate, centerX, y, { align: 'center' });
      y += 8;
      doc.setFontSize(10.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(cherry.r, cherry.g, cherry.b);
      doc.text(sanitizeForPdf(S.subtitle), centerX, y, { align: 'center' });

      // Double rule
      y += 5;
      const ruleL = margin + 35;
      const ruleR = pageWidth - margin - 35;
      doc.setDrawColor(borderC.r, borderC.g, borderC.b);
      doc.setLineWidth(0.35);
      doc.line(ruleL, y, ruleR, y);
      doc.setLineWidth(0.12);
      doc.line(ruleL, y + 1.4, ruleR, y + 1.4);

      // ═══ INFO PANEL ═══
      y += 7;

      const delayLabel =
        settlement.sinister_delay_days && settlement.sinister_delay_days > 0
          ? `${settlement.sinister_delay_days} ${S.days}`
          : S.noDelay;
      const dateSoldLabel = settlement.date_sold
        ? new Date(settlement.date_sold).toLocaleDateString(dateLocale, {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          })
        : '-';

      // Build a flat list of info items, then chunk into rows of 3.
      const flatItems: Array<{ label: string; value: string }> = [
        {
          label: S.container,
          value:
            settlement.container_number || settlement.reference_number || '-',
        },
        { label: S.receiver, value: settlement.receiver || '-' },
        {
          label: S.saleMarket,
          value: settlement.market?.name || String(settlement.market_id || '-'),
        },
        {
          label: S.vessel,
          value: `${settlement.ship_trip?.ship?.name || '-'} (${localShipTypeLabel(settlement.ship_trip?.ship?.type)})`,
        },
        { label: S.delay, value: delayLabel },
        ...(settlement.sinister_case_number
          ? [{ label: S.caseNumber, value: settlement.sinister_case_number }]
          : []),
        { label: S.saleDate, value: dateSoldLabel },
        { label: S.season, value: settlement.season || '-' },
      ];

      // Conditional metadata (only when relevant analysis is included)
      if (includeMarket && marketRegion) {
        flatItems.push({
          label: S.comparableMarket,
          value:
            marketRegion === 'SH'
              ? 'Shanghai (SH)'
              : marketRegion === 'GZ'
                ? 'Guangzhou (GZ)'
                : marketRegion,
        });
      }
      if (includeExporter || includeIndustry) {
        flatItems.push({ label: S.incoterm, value: currencyMode });
      }
      if (includeIndustry) {
        flatItems.push({
          label: S.industryWeighting,
          value: poolMethodology === 'by_volume' ? S.byVolume : S.byExporter,
        });
      }
      if (
        includeMarket &&
        marketDisplayCurrency === 'USD' &&
        marketExchangeRate
      ) {
        flatItems.push({
          label: S.fxLabel,
          value: Number(marketExchangeRate).toFixed(4),
        });
      }

      // Chunk into rows of 3, then size the panel to fit
      const infoItems: Array<Array<{ label: string; value: string }>> = [];
      for (let i = 0; i < flatItems.length; i += 3) {
        infoItems.push(flatItems.slice(i, i + 3));
      }
      const rowCount = infoItems.length;
      const pad = 6;
      const rowH = 8;
      const panelH = pad * 2 + rowCount * rowH - 1;

      doc.setFillColor(faintBg.r, faintBg.g, faintBg.b);
      doc.setDrawColor(borderC.r, borderC.g, borderC.b);
      doc.setLineWidth(0.2);
      doc.roundedRect(margin, y, contentWidth, panelH, 2.5, 2.5, 'FD');

      const cols = [
        margin + pad,
        margin + contentWidth * 0.35,
        margin + contentWidth * 0.67,
      ];
      const rowYs = Array.from(
        { length: rowCount },
        (_, i) => y + pad + i * rowH
      );

      infoItems.forEach((row, ri) => {
        row.forEach((item, ci) => {
          doc.setFontSize(5.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(muted.r, muted.g, muted.b);
          doc.text(item.label, cols[ci], rowYs[ri]);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(slate.r, slate.g, slate.b);
          const maxW = contentWidth * 0.3;
          const val =
            doc.getTextWidth(item.value) > maxW
              ? item.value.substring(0, 30) + '...'
              : item.value;
          doc.text(val, cols[ci], rowYs[ri] + 3.6);
        });
      });

      // ═══ KPI CARDS ═══
      y += panelH + 8;
      const kpiGap = 4;
      const visibleKpis: Array<{
        label: string;
        value: string;
        accent: { r: number; g: number; b: number };
      }> = [];
      const kpiPnlMarket = (() => {
        const v = convertMarketValue(summaryTotals.lossMarket);
        if (v == null) return '-';
        const f =
          marketDisplayCurrency === 'RMB'
            ? formatRmb(v, 0)
            : formatMoneyUSD(v, { decimals: 0 } as any);
        return v > 0 ? `+${f}` : f;
      })();
      const kpiPnlUsd = (n: number) => {
        const f = formatMoneyUSD(n, { decimals: 0 } as any);
        return n > 0 ? `+${f}` : f;
      };
      if (includeMarket) {
        visibleKpis.push({
          label: `${S.pnlVsMarket} (${marketDisplayCurrency})`,
          value: kpiPnlMarket,
          accent: summaryTotals.lossMarket < 0 ? red : green,
        });
      }
      if (includeExporter) {
        visibleKpis.push({
          label: S.pnlVsExporter,
          value: kpiPnlUsd(summaryTotals.lossExporter),
          accent: summaryTotals.lossExporter < 0 ? red : green,
        });
      }
      if (includeIndustry) {
        visibleKpis.push({
          label: S.pnlVsIndustry,
          value: kpiPnlUsd(summaryTotals.lossIndustry),
          accent: summaryTotals.lossIndustry < 0 ? red : green,
        });
      }
      const kpiCount = Math.max(visibleKpis.length, 1);
      const kpiW = (contentWidth - kpiGap * (kpiCount - 1)) / kpiCount;
      const kpiH = 24;

      visibleKpis.forEach((kpi, i) => {
        const kx = margin + i * (kpiW + kpiGap);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(borderC.r, borderC.g, borderC.b);
        doc.setLineWidth(0.2);
        doc.roundedRect(kx, y, kpiW, kpiH, 2, 2, 'FD');
        doc.setFillColor(kpi.accent.r, kpi.accent.g, kpi.accent.b);
        doc.roundedRect(kx, y, 2.2, kpiH, 2, 0, 'F');
        doc.rect(kx + 1.2, y, 1.2, kpiH, 'F');
        doc.setFontSize(5.2);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(muted.r, muted.g, muted.b);
        doc.text(kpi.label, kx + 7, y + 7);
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(kpi.accent.r, kpi.accent.g, kpi.accent.b);
        doc.text(kpi.value, kx + 7, y + 17.5);
      });

      // ═══ SECTION HELPERS ═══
      y += kpiH + 10;

      const sectionTitle = (label: string, yPos: number) => {
        doc.setFillColor(cherry.r, cherry.g, cherry.b);
        doc.rect(margin, yPos - 3, 2.5, 2.5, 'F');
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(charcoal.r, charcoal.g, charcoal.b);
        doc.text(label, margin + 5.5, yPos);
      };

      const colorDiffCell = (data: any, colIdx: number) => {
        if (data.section === 'body' && data.column.index === colIdx) {
          const raw = data.cell.raw as string;
          if (raw && raw !== '-' && raw !== '0') {
            // Sign convention: negative number = loss (red), positive = gain (green).
            const isLoss = raw.includes('-');
            data.cell.styles.textColor = isLoss
              ? [red.r, red.g, red.b]
              : [green.r, green.g, green.b];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      };

      const miniStyles = {
        styles: {
          fontSize: 7,
          cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
          lineColor: [borderC.r, borderC.g, borderC.b] as [
            number,
            number,
            number,
          ],
          lineWidth: 0.12,
          textColor: [slate.r, slate.g, slate.b] as [number, number, number],
        },
        headStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b] as [
            number,
            number,
            number,
          ],
          textColor: [255, 255, 255] as [number, number, number],
          fontStyle: 'bold' as const,
          fontSize: 6.5,
          cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
        },
        footStyles: {
          fillColor: [charcoal.r, charcoal.g, charcoal.b] as [
            number,
            number,
            number,
          ],
          textColor: [255, 255, 255] as [number, number, number],
          fontStyle: 'bold' as const,
          fontSize: 7,
          cellPadding: { top: 2.5, right: 2, bottom: 2.5, left: 2 },
        },
        alternateRowStyles: {
          fillColor: [faintBg.r, faintBg.g, faintBg.b] as [
            number,
            number,
            number,
          ],
        },
      };

      // ═══ COMPARABLE VESSELS POOL ═══
      // Only when at least one of the pool-based sources (Exporter / Industry)
      // is included AND there's at least one vessel selected. Renders BEFORE
      // the loss summary so the reader knows the universe being compared.
      const hasPoolSources =
        pdfSources.includes('exporter') || pdfSources.includes('industry');
      const selectedShips = hasPoolSources
        ? candidates.filter((c) => selectedTripIds.has(c.id))
        : [];
      if (selectedShips.length > 0) {
        sectionTitle(`${S.poolTitle} (${selectedShips.length})`, y);
        y += 4;
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin, bottom: 18 },
          head: [
            [
              S.tableVessel,
              S.tableType,
              S.tableVoyage,
              S.tablePod,
              S.tableEta,
            ],
          ],
          body: selectedShips.map((s) => [
            sanitizeForPdf(s.ship_name || '-'),
            localShipTypeLabel(s.ship_type),
            sanitizeForPdf(s.voyage_number || '-'),
            sanitizeForPdf(String(s.market_id || '-')),
            s.eta ? new Date(s.eta).toLocaleDateString(dateLocale) : '-',
          ]),
          ...miniStyles,
        });
        y = (doc as any).lastAutoTable?.finalY + 8 || y + 30;
      }

      // ═══ SUMMARY: Variety + Caliber with grouped headers ═══
      sectionTitle(S.summaryTitle, y);
      y += 4;

      // Build grouped header rows.
      // Layout:
      //   Variety | Size | Kilos |
      //     Venta Propio ({mktCur})/kg (standalone, rowSpan 2) |
      //     Own {currencyMode} (USD)/kg (standalone, rowSpan 2) |
      //     vs Market ({mktCur}) [Comp | P&L] |
      //     vs Exporter (USD) [Comp | P&L] |
      //     vs Industry (USD) [Comp | P&L]
      const topHeader: any[] = [
        { content: S.headVariety, rowSpan: 2, styles: { valign: 'middle' } },
        { content: S.headSize, rowSpan: 2, styles: { valign: 'middle' } },
        {
          content: S.headKilos,
          rowSpan: 2,
          styles: { valign: 'middle', halign: 'right' },
        },
      ];
      const subHeader: string[] = [];
      // Loss column indexes (for color highlight)
      const lossColIdxs: number[] = [];
      let baseIdx = 3;

      // Standalone "Venta Propio" column (Mercado side, displayed currency)
      if (includeMarket) {
        topHeader.push({
          content: `${S.saleOwn}\n(${marketDisplayCurrency})/kg`,
          rowSpan: 2,
          styles: { valign: 'middle', halign: 'right' },
        });
        baseIdx += 1;
      }
      // Standalone "FOB/CIF Propio" column — only if exporter or industry is shown
      const showOwnUsd = includeExporter || includeIndustry;
      if (showOwnUsd) {
        topHeader.push({
          content: `${currencyMode} ${isES ? 'Propio' : 'Own'}\n(USD)/kg`,
          rowSpan: 2,
          styles: { valign: 'middle', halign: 'right' },
        });
        baseIdx += 1;
      }
      if (includeMarket) {
        topHeader.push({
          content: `${S.saleVsMarket} (${marketDisplayCurrency})`,
          colSpan: 2,
          styles: { halign: 'center' },
        });
        subHeader.push(S.headComp, S.headPnl);
        lossColIdxs.push(baseIdx + 1);
        baseIdx += 2;
      }
      if (includeExporter) {
        topHeader.push({
          content: `${currencyMode} ${S.vsExporter} (USD)`,
          colSpan: 2,
          styles: { halign: 'center' },
        });
        subHeader.push(S.headComp, S.headPnl);
        lossColIdxs.push(baseIdx + 1);
        baseIdx += 2;
      }
      if (includeIndustry) {
        topHeader.push({
          content: `${currencyMode} ${S.vsIndustry} (USD)`,
          colSpan: 2,
          styles: { halign: 'center' },
        });
        subHeader.push(S.headComp, S.headPnl);
        lossColIdxs.push(baseIdx + 1);
        baseIdx += 2;
      }

      // Mercado plain values (no sign prefix) — for V.Propio + Comp columns.
      // Prices use 4 decimals so users can verify kilos × (own − comp) with
      // less rounding error.
      const fmtMarketPrice = (v: number | null) => {
        const converted = convertMarketValue(v);
        if (converted == null) return '-';
        return marketDisplayCurrency === 'RMB'
          ? formatRmb(converted, 4)
          : formatMoneyUSD(converted, { decimals: 4 } as any);
      };
      // Mercado P&L (with explicit sign for positive)
      const fmtMarketPnl = (v: number | null) => {
        const converted = convertMarketValue(v);
        if (converted == null) return '-';
        const formatted =
          marketDisplayCurrency === 'RMB'
            ? formatRmb(converted, 0)
            : formatMoneyUSD(converted, { decimals: 0 } as any);
        return converted > 0 ? `+${formatted}` : formatted;
      };
      const fmtUsd4 = (v: number | null) =>
        v == null ? '-' : formatMoneyUSD(v, { decimals: 4 } as any);
      // USD P&L (with explicit sign for positive)
      const fmtUsdPnl = (v: number | null) => {
        if (v == null) return '-';
        const formatted = formatMoneyUSD(v, { decimals: 0 } as any);
        return v > 0 ? `+${formatted}` : formatted;
      };

      const summaryBody = summary.map((r) => {
        const row: (string | number)[] = [
          r.variety,
          r.caliber,
          Math.round(r.kilos).toLocaleString('es-CL'),
        ];
        // Venta Propio (market side)
        if (includeMarket) {
          row.push(fmtMarketPrice(r.actualMarketRmb));
        }
        // FOB/CIF Propio (USD)
        if (showOwnUsd) {
          row.push(fmtUsd4(r.actualUsdPerKg));
        }
        // Analysis pairs
        if (includeMarket) {
          row.push(
            fmtMarketPrice(r.benchmarkMarket),
            fmtMarketPnl(r.lossByMarket)
          );
        }
        if (includeExporter) {
          row.push(fmtUsd4(r.benchmarkExporter), fmtUsdPnl(r.lossByExporter));
        }
        if (includeIndustry) {
          row.push(fmtUsd4(r.benchmarkIndustry), fmtUsdPnl(r.lossByIndustry));
        }
        return row;
      });

      const totalsRow: (string | number)[] = [
        S.total,
        '',
        Math.round(summaryTotals.kilos).toLocaleString('es-CL'),
      ];
      // Venta Propio total (blank — not an aggregable per-kg value)
      if (includeMarket) totalsRow.push('');
      // FOB/CIF Propio total (blank — same reason)
      if (showOwnUsd) totalsRow.push('');
      // Analysis pairs: comparable blank, P&L total
      if (includeMarket) {
        totalsRow.push('', fmtMarketPnl(summaryTotals.lossMarket));
      }
      if (includeExporter) {
        totalsRow.push('', fmtUsdPnl(summaryTotals.lossExporter));
      }
      if (includeIndustry) {
        totalsRow.push('', fmtUsdPnl(summaryTotals.lossIndustry));
      }

      // Right-align all numeric columns (everything from index 2 on)
      const columnStyles: Record<number, any> = {};
      for (let i = 2; i < baseIdx; i++) {
        columnStyles[i] = { halign: 'right' as const };
      }

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin, bottom: 18 },
        head: [topHeader, subHeader],
        body: summaryBody,
        foot: [totalsRow.map((c) => String(c))],
        ...miniStyles,
        columnStyles,
        didParseCell: (data: any) => {
          lossColIdxs.forEach((idx) => colorDiffCell(data, idx));
        },
      });

      // Per-source detail tables — temporarily disabled. The summary already
      // shows benchmark + loss per (variety, caliber) for all 3 sources.

      let cursorY = (doc as any).lastAutoTable?.finalY + 10 || y + 60;

      // ═══ OPTIONAL PER-LINE DETAIL BREAKDOWN ═══
      if (pdfIncludeDetail && detailLines.length > 0) {
        if (cursorY > pageHeight - 60) {
          doc.addPage();
          cursorY = 20;
        }
        sectionTitle(S.perLineTitle, cursorY);
        cursorY += 4;

        const detailHead: any[] = [
          { content: S.headVariety, rowSpan: 2, styles: { valign: 'middle' } },
          {
            content: isES ? 'Cal.' : 'Size',
            rowSpan: 2,
            styles: { valign: 'middle' },
          },
          { content: S.headColor, rowSpan: 2, styles: { valign: 'middle' } },
          {
            content: S.headKilos,
            rowSpan: 2,
            styles: { valign: 'middle', halign: 'right' },
          },
        ];
        const detailSubHead: string[] = [];
        const detailLossCols: number[] = [];
        let dIdx = 4;
        // Standalone Venta Propio (market side)
        if (includeMarket) {
          detailHead.push({
            content: `${S.saleOwn}\n(${marketDisplayCurrency})/kg`,
            rowSpan: 2,
            styles: { valign: 'middle', halign: 'right' },
          });
          dIdx += 1;
        }
        const dShowOwnUsd = includeExporter || includeIndustry;
        if (dShowOwnUsd) {
          detailHead.push({
            content: `${currencyMode} ${isES ? 'Propio' : 'Own'}\n(USD)/kg`,
            rowSpan: 2,
            styles: { valign: 'middle', halign: 'right' },
          });
          dIdx += 1;
        }
        if (includeMarket) {
          detailHead.push({
            content: `${S.saleVsMarket} (${marketDisplayCurrency})`,
            colSpan: 2,
            styles: { halign: 'center' },
          });
          detailSubHead.push(S.headComp, S.headPnl);
          detailLossCols.push(dIdx + 1);
          dIdx += 2;
        }
        if (includeExporter) {
          detailHead.push({
            content: `${currencyMode} ${S.vsExporter} (USD)`,
            colSpan: 2,
            styles: { halign: 'center' },
          });
          detailSubHead.push(S.headComp, S.headPnl);
          detailLossCols.push(dIdx + 1);
          dIdx += 2;
        }
        if (includeIndustry) {
          detailHead.push({
            content: `${currencyMode} ${S.vsIndustry} (USD)`,
            colSpan: 2,
            styles: { halign: 'center' },
          });
          detailSubHead.push(S.headComp, S.headPnl);
          detailLossCols.push(dIdx + 1);
          dIdx += 2;
        }

        const fmtMarketPriceLine = (v: number | null) => {
          const conv = convertMarketValue(v);
          if (conv == null) return '-';
          return marketDisplayCurrency === 'RMB'
            ? formatRmb(conv, 4)
            : formatMoneyUSD(conv, { decimals: 4 } as any);
        };
        const fmtMarketPnlLine = (v: number | null) => {
          const conv = convertMarketValue(v);
          if (conv == null) return '-';
          const f =
            marketDisplayCurrency === 'RMB'
              ? formatRmb(conv, 0)
              : formatMoneyUSD(conv, { decimals: 0 } as any);
          return conv > 0 ? `+${f}` : f;
        };
        const fmtUsdLine = (v: number | null) =>
          v == null ? '-' : formatMoneyUSD(v, { decimals: 4 } as any);
        const fmtUsdPnlLine = (v: number | null) => {
          if (v == null) return '-';
          const f = formatMoneyUSD(v, { decimals: 0 } as any);
          return v > 0 ? `+${f}` : f;
        };

        const detailBody = detailLines.map((d) => {
          const row: (string | number)[] = [
            d.variety,
            d.caliber,
            d.color,
            Math.round(d.kilos).toLocaleString('es-CL'),
          ];
          // Venta Propio (market side)
          if (includeMarket) {
            row.push(fmtMarketPriceLine(d.actualMarketRmb));
          }
          // FOB/CIF Propio (USD)
          if (dShowOwnUsd) {
            row.push(fmtUsdLine(d.actualUsdPerKg));
          }
          // Analysis pairs
          if (includeMarket) {
            row.push(
              fmtMarketPriceLine(d.benchmarkMarket),
              fmtMarketPnlLine(d.lossMarketRmb)
            );
          }
          if (includeExporter) {
            row.push(
              fmtUsdLine(d.benchmarkExporter),
              fmtUsdPnlLine(d.lossExporterUsd)
            );
          }
          if (includeIndustry) {
            row.push(
              fmtUsdLine(d.benchmarkIndustry),
              fmtUsdPnlLine(d.lossIndustryUsd)
            );
          }
          return row;
        });

        const detailColStyles: Record<number, any> = {};
        for (let i = 3; i < dIdx; i++) {
          detailColStyles[i] = { halign: 'right' as const };
        }

        autoTable(doc, {
          startY: cursorY,
          margin: { left: margin, right: margin, bottom: 18 },
          head: [detailHead, detailSubHead],
          body: detailBody,
          ...miniStyles,
          styles: { ...miniStyles.styles, fontSize: 6.5 },
          columnStyles: detailColStyles,
          didParseCell: (data: any) => {
            detailLossCols.forEach((idx) => colorDiffCell(data, idx));
          },
        });
        cursorY = (doc as any).lastAutoTable?.finalY + 10 || cursorY + 50;
      }

      // ═══ METHODOLOGY APPENDIX ═══
      const refDateLabel = (() => {
        if (!settlement.date_sold) return '-';
        const d = new Date(settlement.date_sold);
        d.setDate(d.getDate() - (settlement.sinister_delay_days || 0));
        return d.toLocaleDateString(dateLocale, {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
      })();
      const saleMarketLabel =
        settlement.market?.name || String(settlement.market_id || '-');
      const exporterLabel =
        settlement.exporter?.name ||
        (settlement as any).exporter?.excel_name ||
        '-';
      // The Market source compares against a SINGLE date — the reference date.
      // No window / prior-days lookback is applied (configuration is hardcoded
      // to 0 in the UI). When days_delay > 0, the reference date itself shifts
      // to "the day the sale would have happened", but the query is still
      // against that one date.
      const poolSize = selectedTripIds.size;

      const methodologyBlocks: Array<{ title: string; body: string }> = [];
      const fxRate =
        marketExchangeRate || settlement.exchange_rate || '—';
      const days = settlement.sinister_delay_days || 0;
      if (includeMarket) {
        if (isES) {
          const displayCurrencyNote =
            marketDisplayCurrency === 'USD'
              ? ` Los valores luego se convierten a USD para mostrarse usando un tipo de cambio definido por el usuario (${fxRate} RMB/USD); el cálculo subyacente se mantiene en RMB.`
              : '';
          methodologyBlocks.push({
            title: S.methodPnlMarket,
            body:
              `Elegibilidad: este análisis corre solamente cuando la liquidación afectada está denominada en RMB; el usuario luego selecciona la región de mercado china comparable (Shanghai o Guangzhou). ` +
              `Fecha de referencia = ${refDateLabel}, calculada como la fecha real de venta menos los días de retraso registrados (${days} día(s)) — cuando no hay retraso equivale a la fecha de venta. ` +
              `Para cada grupo (variedad, calibre), el promedio propio ponderado por kilos (suma local_total_price / suma kilos, en RMB) se compara contra la MEDIANA de market_data.price_kg ` +
              `para la misma variedad + calibre observada en la región seleccionada EN ESA FECHA EXACTA (sin ventana de días previos). Todas las listas de exportadoras se incluyen en la mediana (sin exclusión). ` +
              `P&L = (real − mediana) × kilos en RMB.${displayCurrencyNote}`,
          });
        } else {
          const displayCurrencyNote =
            marketDisplayCurrency === 'USD'
              ? ` Values are then converted to USD for display using a user-supplied exchange rate (${fxRate} RMB/USD); the underlying calculation remains in RMB.`
              : '';
          methodologyBlocks.push({
            title: S.methodPnlMarket,
            body:
              `Eligibility: this analysis runs only when the affected settlement is denominated in RMB; the user then selects the comparable Chinese market region (Shanghai or Guangzhou). ` +
              `Reference date = ${refDateLabel}, computed as the actual sale date minus the recorded incident delay (${days} day(s)) — when there is no delay this equals the sale date itself. ` +
              `For each (variety, size) group, the affected settlement's own kilos-weighted average price (sum local_total_price / sum kilos, in RMB) is compared against the MEDIAN of market_data.price_kg ` +
              `for the same variety + size observed in the selected region ON THAT EXACT DATE (no prior-days window). All exporter listings are included in the median (no exclusion). ` +
              `P&L = (actual − median) × kilos in RMB.${displayCurrencyNote}`,
          });
        }
      }
      const industryMethodNoteEN =
        poolMethodology === 'by_volume'
          ? "Quartiles use a volume-weighted percentile: each peer-exporter point is sorted by price-per-kg and the cumulative-weight curve (summing each exporter's kilos) is interpolated linearly to locate the 25th/50th/75th percentiles. Larger exporters shift the median proportionally to their volume."
          : 'Quartiles are positional, treating each peer exporter as one equal-weight point: sorted by price-per-kg, the positional Q1 / median / Q3 are taken. Exporter volume does not influence the median.';
      const industryMethodNoteES =
        poolMethodology === 'by_volume'
          ? 'Los cuartiles usan un percentil ponderado por volumen: cada punto de una exportadora par se ordena por precio por kilo y la curva de pesos acumulados (sumando los kilos de cada exportadora) se interpola linealmente para ubicar los percentiles 25 / 50 / 75. Las exportadoras más grandes mueven la mediana proporcionalmente a su volumen.'
          : 'Los cuartiles son posicionales, tratando a cada exportadora par como un punto de peso igual: ordenados por precio por kilo, se toman Q1 / mediana / Q3 posicionales. El volumen de la exportadora no influye en la mediana.';
      if (includeExporter) {
        if (isES) {
          methodologyBlocks.push({
            title: S.methodPnlExporter,
            body:
              `Pool: ${poolSize} viaje(s) de nave seleccionado(s) manualmente que arriban al mismo mercado de venta (${saleMarketLabel}) o al mismo puerto de descarga que la nave afectada, con ETA dentro de ±7 días de la fecha de venta o ETA del siniestro ajustadas por los días de retraso registrados. ` +
              `Filtros de calidad del pool: las liquidaciones deben estar aprobadas, tener tipo de cambio no nulo y total_freight_usd > 0; los siniestros se excluyen. ` +
              `Para cada grupo (variedad, calibre), el benchmark es el promedio ponderado por kilos sobre todas las líneas de la exportadora afectada (${exporterLabel}) en el pool que coinciden — es decir, suma(${currencyMode}) / suma(kilos). ` +
              `P&L = (real ${currencyMode}/kg − benchmark) × kilos en USD. ` +
              `${currencyMode === 'CIF' ? 'CIF se calcula como FOB + flete por línea.' : 'FOB se toma directo de la línea de la liquidación.'}`,
          });
        } else {
          methodologyBlocks.push({
            title: S.methodPnlExporter,
            body:
              `Pool: ${poolSize} ship trip(s) selected manually that arrive at the same sale market (${saleMarketLabel}) OR the same port of discharge as the affected vessel, with ETA within ±7 days of either the affected sale date or ETA after adjusting for the recorded incident delay. ` +
              `Pool quality filters: settlements must be approved, have a non-null exchange rate and total_freight_usd > 0; incident settlements are excluded. ` +
              `For each (variety, size) group, the benchmark is a kilos-weighted average over all of the affected exporter (${exporterLabel})'s settlement details in the pool that match — that is, sum(${currencyMode}) / sum(kilos). ` +
              `P&L = (actual ${currencyMode}/kg − benchmark) × kilos in USD. ` +
              `${currencyMode === 'CIF' ? 'CIF is computed as FOB + freight per detail line.' : 'FOB is taken directly from the settlement detail.'}`,
          });
        }
      }
      if (includeIndustry) {
        if (isES) {
          methodologyBlocks.push({
            title: S.methodPnlIndustry,
            body:
              `Misma definición de pool que P&L vs Exportadora (mismo conjunto de naves, ventana de ETA, filtros de calidad y exclusión de siniestros), pero la exportadora afectada (${exporterLabel}) se excluye — sólo contribuyen las exportadoras pares. Las exportadoras que optaron por no compartir datos de industria para esta especie/temporada también se remueven del pool. ` +
              `Para cada grupo (variedad, calibre): cada exportadora par primero se colapsa a UN punto igual a su propio promedio ponderado por kilos en ${currencyMode}/kg dentro del pool. El benchmark es luego la MEDIANA sobre esos puntos por exportadora. ${industryMethodNoteES} ` +
              `P&L = (real ${currencyMode}/kg − mediana) × kilos en USD.`,
          });
        } else {
          methodologyBlocks.push({
            title: S.methodPnlIndustry,
            body:
              `Same pool definition as P&L vs Exporter (same vessel set, ETA window, quality filters and incident exclusion), but the affected exporter (${exporterLabel}) is excluded — only peer exporters contribute. Exporters that have opted out of industry data sharing for this species/season are also removed from the pool. ` +
              `For each (variety, size) group: every peer exporter is first collapsed to ONE point equal to their own kilos-weighted average ${currencyMode}/kg in the pool. The benchmark is then the MEDIAN of those per-exporter points. ${industryMethodNoteEN} ` +
              `P&L = (actual ${currencyMode}/kg − median) × kilos in USD.`,
          });
        }
      }

      // Sanitize all methodology blocks once
      const safeBlocks = methodologyBlocks.map((b) => ({
        title: sanitizeForPdf(b.title),
        body: sanitizeForPdf(b.body),
      }));

      // Final disclaimer (sanitized) — included in the same card so it shares
      // padding and visual grouping with the methodology blocks.
      const disclaimer = sanitizeForPdf(
        isES
          ? 'Este certificado se genera automáticamente a partir de los datos de liquidación y de mercado ' +
              'disponibles al momento de su emisión. Las cifras son indicativas y están sujetas a revisión si surge nueva información. ' +
              'Convención de signos: los valores P&L positivos representan ganancias realizadas, los negativos representan pérdidas (respecto al benchmark). ' +
              'El P&L se calcula con precisión completa a partir de sumas crudas (suma valor / suma kilos), luego se redondea para mostrar. Multiplicar manualmente ' +
              'kilos × (Propio - Comp.) con los precios de 4 decimales mostrados puede dar pequeñas diferencias por redondeo; ' +
              'el número de la columna P&L es el correcto.'
          : 'This certificate is generated automatically from the underlying settlement and market data ' +
              'available at the time of issuance. Figures are indicative and subject to revision should new information arise. ' +
              'Sign convention: positive P&L values represent realized gains, negative values represent losses (relative to the benchmark). ' +
              'P&L is computed at full precision from raw sums (sum value / sum kilos), then rounded for display. Manually multiplying ' +
              'kilos × (Own - Comp.) using the displayed 4-decimal prices may yield small differences vs the P&L column due to rounding; ' +
              'the P&L column is the authoritative figure.'
      );

      // ---- Height accounting (so the card frame fits everything) ----
      const innerWidth = contentWidth - 8; // 4mm padding each side
      const blockHeight = (text: string) => {
        const lines = doc.splitTextToSize(text, innerWidth);
        return lines.length * 3.2;
      };
      const titleH = 3.6;
      const blockGap = 2.5;
      const blocksTotalH = safeBlocks.reduce(
        (acc, b) => acc + titleH + blockHeight(b.body) + blockGap,
        0
      );
      const disclaimerLines = doc.splitTextToSize(disclaimer, innerWidth);
      const disclaimerH = disclaimerLines.length * 2.6;
      const cardTopPad = 5;
      const cardBottomPad = 4;
      const disclaimerGap = 3;
      const cardHeight =
        cardTopPad + blocksTotalH + disclaimerGap + disclaimerH + cardBottomPad;

      // Section title + card
      const totalSectionH = 6 + cardHeight;
      if (cursorY + totalSectionH > pageHeight - 18) {
        doc.addPage();
        cursorY = 20;
      }
      sectionTitle(S.methodologyTitle, cursorY);
      cursorY += 6;
      const methCardY = cursorY;
      doc.setFillColor(faintBg.r, faintBg.g, faintBg.b);
      doc.setDrawColor(borderC.r, borderC.g, borderC.b);
      doc.setLineWidth(0.2);
      doc.roundedRect(margin, methCardY, contentWidth, cardHeight, 2, 2, 'FD');

      // Render methodology blocks
      let methY = methCardY + cardTopPad;
      safeBlocks.forEach((block) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(cherry.r, cherry.g, cherry.b);
        doc.text(block.title, margin + 4, methY);
        methY += titleH;

        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(slate.r, slate.g, slate.b);
        const lines = doc.splitTextToSize(block.body, innerWidth);
        lines.forEach((line: string) => {
          doc.text(line, margin + 4, methY);
          methY += 3.2;
        });
        methY += blockGap;
      });

      // Disclaimer — same horizontal padding as blocks, just a different style
      methY += disclaimerGap - blockGap; // tighten gap so it sits ~3mm below last block
      doc.setFontSize(6);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(muted.r, muted.g, muted.b);
      disclaimerLines.forEach((line: string) => {
        doc.text(line, margin + 4, methY);
        methY += 2.6;
      });
      cursorY = methCardY + cardHeight + 4;

      drawFooter();

      const filePrefix = isES ? 'Certificado_Siniestro' : 'Certificate_Incident';
      const fileName = `${filePrefix}_${settlement.container_number || settlement.id}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error exporting incident PDF:', error);
      toast.error(t('Error al generar el PDF'));
    }
  };

  if (isLoading) {
    return (
      <LoadingState />
    );
  }

  if (!settlement) {
    return (
      <PageLayout>
        <BackButton href='/settlements/siniestros' label={t('Volver')} />
        <EmptyState
          icon='tabler:file-search'
          title={t('Siniestro no encontrado.')}
        />
      </PageLayout>
    );
  }

  const shipType = settlement.ship_trip?.ship?.type || '-';

  const reloadHistoryCount = async () => {
    if (!settlement?.id) return;
    const countsMap = await getSettlementHistoryCounts([settlement.id]);
    setHistoryCount(countsMap.get(settlement.id) || 0);
  };

  return (
    <PageLayout>
      <BackButton
        href='/settlements/siniestros'
        label={t('Volver al listado')}
      />

      <PageHeader
        title={settlement.container_number || settlement.reference_number}
        titleAddon={
          <Chip
            size='sm'
            variant='flat'
            startContent={
              <Icon icon='tabler:alert-triangle' className='ml-1 text-danger' />
            }
          >
            {t('Certificado de siniestro')}
          </Chip>
        }
        description={
          <>
            {settlement.receiver} · {settlement.ship_trip?.ship?.name} ·{' '}
            {shipTypeLabel(shipType)}
          </>
        }
        actions={
          <div className='flex flex-wrap gap-2 lg:justify-end'>
            {settlement.sinister_responsible && (
              <Chip
                color='warning'
                variant='flat'
                startContent={
                  <Icon icon='tabler:user-shield' className='ml-1 text-sm' />
                }
              >
                {t(settlement.sinister_responsible)}
              </Chip>
            )}
            {settlement.sinister_delay_days &&
            settlement.sinister_delay_days > 0 ? (
              <Chip
                color='danger'
                variant='flat'
                startContent={
                  <Icon icon='tabler:clock-pause' className='ml-1 text-sm' />
                }
              >
                {t('Retraso: {{count}} días', {
                  count: settlement.sinister_delay_days,
                })}
              </Chip>
            ) : (
              <Chip
                color='default'
                variant='flat'
                startContent={
                  <Icon icon='tabler:clock-check' className='ml-1 text-sm' />
                }
              >
                {t('Sin retraso')}
              </Chip>
            )}
            {settlement.sinister_case_number && (
              <Chip
                variant='flat'
                startContent={
                  <Icon icon='tabler:hash' className='ml-1 text-sm' />
                }
              >
                {t('Número de Caso')}: {settlement.sinister_case_number}
              </Chip>
            )}
            <Chip
              variant='flat'
              startContent={
                <Icon icon='tabler:map-pin' className='ml-1 text-sm' />
              }
            >
              {settlement.market?.name || settlement.market_id || '-'}
            </Chip>
            <BitacoraButton
              count={historyCount}
              label={t('Bitácora')}
              onPress={() => setIsHistoryOpen(true)}
            />
            <SinisterEditPopover
              settlement={settlement}
              onSave={(responsible, delayDays, caseNumber) => {
                setSettlement((prev) =>
                  prev
                    ? {
                        ...prev,
                        sinister_responsible: responsible,
                        sinister_delay_days: delayDays,
                        sinister_case_number: caseNumber,
                      }
                    : prev
                );
              }}
              trigger={
                <Button
                  size='sm'
                  variant='flat'
                  startContent={<Icon icon='tabler:edit' />}
                >
                  {t('Editar')}
                </Button>
              }
            />
          </div>
        }
      />

      <Card shadow='sm' className='mb-6'>
        <div className='grid grid-cols-2 md:grid-cols-4 divide-x divide-default-200 dark:divide-default-100'>
          <MetaCell
            icon='tabler:calendar'
            label={t('Fecha de venta')}
            value={
              settlement.date_sold
                ? new Date(settlement.date_sold).toLocaleDateString()
                : '-'
            }
          />
          <MetaCell
            icon='tabler:building-store'
            label={t('Mercado de venta')}
            value={
              settlement.market?.name || String(settlement.market_id || '-')
            }
          />
          <MetaCell
            icon='tabler:anchor'
            label={t('ETA Nave')}
            value={
              settlement.ship_trip?.eta
                ? new Date(settlement.ship_trip.eta).toLocaleDateString()
                : '-'
            }
          />
          <MetaCell
            icon='tabler:file-invoice'
            label={t('Incoterm original')}
            value={settlement.original_incoterm || '-'}
          />
        </div>
      </Card>

      {/* SECTION: Análisis disponibles */}
      <SectionHeader
        eyebrow={t('paso 1')}
        title={t('Análisis disponibles')}
        subtitle={t(
          'Siempre se ejecutan los 3. Al exportar el certificado podrás elegir cuáles incluir.'
        )}
      />
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
        {(['market', 'exporter', 'industry'] as SinisterSource[]).map(
          (source) => {
            const meta = SOURCE_META[source];
            const isMarket = source === 'market';
            const settlementCurrency = (
              settlement.local_currency || ''
            ).toUpperCase();
            const isRmb = settlementCurrency === 'RMB';
            return (
              <Card key={source} shadow='sm' className='h-full'>
                <CardBody className='gap-3 p-6'>
                  <div className='flex items-center gap-3'>
                    <div className='rounded-medium border border-default-200 p-2.5'>
                      <Icon
                        icon={meta.icon}
                        className={`text-2xl ${meta.accent}`}
                      />
                    </div>
                    <div>
                      <div className={`font-semibold text-base ${meta.accent}`}>
                        {meta.title}
                      </div>
                      <div className='text-[11px] uppercase tracking-wider text-default-500 font-medium'>
                        {meta.short}
                      </div>
                    </div>
                  </div>
                  <p className='text-sm text-foreground/80 leading-relaxed'>
                    {meta.description}
                  </p>
                  <ul className='text-xs text-default-600 dark:text-default-500 space-y-1 mt-1'>
                    {meta.bullets.map((b) => (
                      <li key={b} className='flex gap-1.5 items-start'>
                        <Icon
                          icon='tabler:point-filled'
                          className={`shrink-0 mt-0.5 ${meta.accent}`}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Mercado: inline configuration */}
                  {isMarket && (
                    <>
                      <Divider className='my-1' />
                      <div className='flex flex-col gap-2'>
                        <div className='text-[11px] uppercase tracking-wider text-default-500 font-semibold'>
                          {t('Configuración')}
                        </div>
                        {!isRmb ? (
                          <div className='text-xs flex items-start gap-1.5 text-warning-600 dark:text-warning-400'>
                            <Icon
                              icon='tabler:alert-triangle-filled'
                              className='shrink-0 mt-0.5'
                            />
                            <span>
                              {t(
                                'Sólo aplica a liquidaciones en RMB. Esta liquidación está en'
                              )}{' '}
                              <strong>{settlementCurrency || '—'}</strong>.
                            </span>
                          </div>
                        ) : (
                          <>
                            <Input
                              type='date'
                              size='sm'
                              label={t('Fecha de venta a comparar')}
                              labelPlacement='outside'
                              value={marketRefDate}
                              onChange={(e) => setMarketRefDate(e.target.value)}
                              description={
                                settlement.sinister_delay_days &&
                                settlement.sinister_delay_days > 0
                                  ? t(
                                      'Sugerido: fecha de venta − {{count}} días de retraso.',
                                      {
                                        count: settlement.sinister_delay_days,
                                      }
                                    )
                                  : t('Sugerido: fecha de venta (sin retraso).')
                              }
                            />
                            <Select
                              size='sm'
                              label={t('Mercado a comparar')}
                              labelPlacement='outside'
                              placeholder={t('Elige un mercado comparable')}
                              isRequired
                              isInvalid={!marketRegion}
                              selectedKeys={marketRegion ? [marketRegion] : []}
                              onChange={(e) =>
                                setMarketRegion(
                                  (e.target.value || '') as 'SH' | 'GZ' | ''
                                )
                              }
                              errorMessage={
                                !marketRegion
                                  ? t('Obligatorio para el análisis vs Mercado.')
                                  : undefined
                              }
                              description={
                                marketRegion
                                  ? settlement.market_id === 'CNSHA' ||
                                    settlement.market_id === 'CNCAN'
                                    ? t(
                                        'Sugerido: {{market}} (mercado de venta).',
                                        {
                                          market:
                                            settlement.market_id === 'CNSHA'
                                              ? 'Shanghai (SH)'
                                              : 'Guangzhou (GZ)',
                                        }
                                      )
                                    : t(
                                        'El mercado de venta no es SH ni GZ. Selecciona contra cuál comparar.'
                                      )
                                  : undefined
                              }
                            >
                              <SelectItem key='SH' value='SH'>
                                Shanghai (SH)
                              </SelectItem>
                              <SelectItem key='GZ' value='GZ'>
                                Guangzhou (GZ)
                              </SelectItem>
                            </Select>

                            {/* Display currency toggle */}
                            <div>
                              <div className='text-xs text-default-500 mb-1'>
                                {t('Mostrar valores en')}
                              </div>
                              <Tabs
                                selectedKey={marketDisplayCurrency}
                                onSelectionChange={(k) =>
                                  setMarketDisplayCurrency(k as 'RMB' | 'USD')
                                }
                                size='sm'
                              >
                                <Tab key='USD' title='USD' />
                                <Tab key='RMB' title='RMB' />
                              </Tabs>
                            </div>
                            {marketDisplayCurrency === 'USD' && (
                              <Input
                                type='number'
                                size='sm'
                                step='0.0001'
                                label={t('Tipo de cambio (RMB → USD)')}
                                labelPlacement='outside'
                                placeholder={t('Ej: 7.20')}
                                value={marketExchangeRate}
                                onChange={(e) =>
                                  setMarketExchangeRate(e.target.value)
                                }
                                description={
                                  settlement.exchange_rate
                                    ? t(
                                        'Sugerido: {{rate}} (de la liquidación). Editable.',
                                        { rate: settlement.exchange_rate }
                                      )
                                    : t(
                                        'Define el RMB/USD a usar para la conversión.'
                                      )
                                }
                              />
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {/* Industria: inline configuration (methodology toggle) */}
                  {/* {source === 'industry' && (
                    <>
                      <Divider className='my-1' />
                      <div className='flex flex-col gap-2'>
                        <div className='text-[11px] uppercase tracking-wider text-default-500 font-semibold'>
                          Configuración
                        </div>
                        <div>
                          <div className='text-xs text-default-500 mb-1'>
                            Ponderación de la mediana
                          </div>
                          <Tabs
                            selectedKey={poolMethodology}
                            onSelectionChange={(k) =>
                              setPoolMethodology(k as SinisterPoolMethodology)
                            }
                            size='sm'
                            color='primary'
                            variant='bordered'
                          >
                            <Tab key='by_volume' title='Por volumen' />
                            <Tab key='by_exporter' title='Por exportadora' />
                          </Tabs>
                          <div className='text-xs text-default-500 mt-1'>
                            {poolMethodology === 'by_volume'
                              ? 'Exportadoras con más kilos influirán más en la mediana.'
                              : 'Cada exportadora cuenta como un punto, sin importar el volumen.'}
                          </div>
                        </div>
                      </div>
                    </>
                  )} */}
                </CardBody>
              </Card>
            );
          }
        )}
      </div>

      {/* SECTION: Common options for vs Exportadora & vs Industria */}
      <Card shadow='sm' className='mb-8'>
        <CardBody className='p-6 flex flex-col gap-5'>
          <div className='flex items-center justify-between flex-wrap gap-2'>
            <div className='flex items-center gap-2 flex-wrap'>
              <div className='text-[11px] uppercase tracking-wider font-semibold text-default-500'>
                {t('Aplica a:')}
              </div>
              <Chip
                size='sm'
                variant='flat'
                className={`${SOURCE_META.exporter.tint} ${SOURCE_META.exporter.accent} border ${SOURCE_META.exporter.ring}`}
                startContent={
                  <Icon
                    icon={SOURCE_META.exporter.icon}
                    className={`ml-1 ${SOURCE_META.exporter.accent}`}
                  />
                }
              >
                {t('vs Exportadora')}
              </Chip>
              <Chip
                size='sm'
                variant='flat'
                className={`${SOURCE_META.industry.tint} ${SOURCE_META.industry.accent} border ${SOURCE_META.industry.ring}`}
                startContent={
                  <Icon
                    icon={SOURCE_META.industry.icon}
                    className={`ml-1 ${SOURCE_META.industry.accent}`}
                  />
                }
              >
                {t('vs Industria')}
              </Chip>
            </div>
          </div>

          <Divider />

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 items-start'>
            <div className='lg:col-span-1'>
              <div className='text-xs uppercase tracking-wider font-semibold text-default-500 mb-2'>
                {t('Incoterm de comparación')}
              </div>
              <Tabs
                selectedKey={currencyMode}
                onSelectionChange={(k) =>
                  setCurrencyMode(k as SinisterCurrencyMode)
                }
                size='md'
              >
                <Tab key='FOB' title='FOB' />
                <Tab key='CIF' title='CIF' />
              </Tabs>
            </div>
          </div>

          <Divider />

          {/* Pool de naves */}
          <div>
            <div className='flex items-start justify-between gap-3 mb-3 flex-wrap'>
              <div>
                <div className='text-xs uppercase tracking-wider font-semibold text-default-500 mb-2'>
                  {t('Pool de naves comparables')}
                </div>
                <div className='text-xs text-default-500 mt-0.5'>
                  {t('Naves vendidas al mismo mercado')} (
                  <span className='font-medium text-foreground/80'>
                    {settlement.market?.name || settlement.market_id}
                  </span>
                  ) <span className='text-default-400'>{t('o')}</span>{' '}
                  {t('arribadas al mismo POD del siniestro')}
                  {settlement.ship_trip?.market?.name && (
                    <>
                      {' '}
                      (
                      <span className='font-medium text-foreground/80'>
                        {settlement.ship_trip.market.name}
                      </span>
                      )
                    </>
                  )}
                  .{' '}
                  {t(
                    'Naves con ETA ±{{days}} días alrededor de la fecha de venta o ETA del siniestro, ajustadas por los días de retraso.',
                    { days: POOL_ETA_WINDOW_DAYS }
                  )}{' '}
                  {poolWindow && (
                    <span className='text-foreground/80'>
                      {t('Ventana: {{start}} → {{end}}.', {
                        start: formatDateOnly(poolWindow.start),
                        end: formatDateOnly(poolWindow.end),
                      })}
                    </span>
                  )}{' '}
                  {t('Los siniestros se excluyen automáticamente del cálculo.')}
                </div>
              </div>
              <Chip
                size='sm'
                variant='flat'
                color={selectedTripIds.size > 0 ? 'primary' : 'default'}
              >
                {t('{{selected}} / {{total}} seleccionadas', {
                  selected: selectedTripIds.size,
                  total: candidates.length,
                })}
              </Chip>
            </div>

            {candidates.length === 0 ? (
              <EmptyState
                title={t('No hay naves candidatas disponibles para este mercado.')}
              />
            ) : (
              <div className='max-h-72 overflow-auto rounded-medium border border-default-200'>
                <Table
                  aria-label={t('Pool de naves')}
                  removeWrapper
                  classNames={{
                    th: 'bg-default-100 dark:bg-default-50 text-default-600',
                  }}
                >
                  <TableHeader>
                    <TableColumn>{t('Sel.')}</TableColumn>
                    <TableColumn>{t('Nave')}</TableColumn>
                    <TableColumn>{t('Tipo')}</TableColumn>
                    <TableColumn>ETA</TableColumn>
                    <TableColumn>{t('Voyage')}</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {candidates.map((c) => {
                      const isOwn = c.id === settlement.ship_trip_id;
                      return (
                        <TableRow
                          key={c.id}
                          className='cursor-pointer hover:bg-default-100 dark:hover:bg-default-50'
                          onClick={() => toggleTrip(c.id)}
                        >
                          <TableCell>
                            <Checkbox
                              isSelected={selectedTripIds.has(c.id)}
                              onValueChange={() => toggleTrip(c.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className='flex items-center gap-2'>
                              <span>{c.ship_name}</span>
                              {isOwn && (
                                <Chip
                                  size='sm'
                                  variant='flat'
                                  color='warning'
                                  className='h-5 text-[10px]'
                                >
                                  {t('esta nave')}
                                </Chip>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{shipTypeLabel(c.ship_type)}</TableCell>
                          <TableCell>
                            {c.eta ? new Date(c.eta).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>{c.voyage_number || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <div className='lg:col-span-1 flex flex-col gap-2 lg:items-center'>
            <Button
              color='primary'
              size='lg'
              onPress={handleCalculate}
              isLoading={isCalculating}
              startContent={<Icon icon='tabler:calculator' />}
              isDisabled={selectedTripIds.size === 0}
              className='font-semibold'
            >
              {t('Calcular perjuicio')}
            </Button>
            {selectedTripIds.size === 0 && (
              <div className='text-xs text-warning-600 dark:text-warning-400 flex items-center gap-1'>
                <Icon icon='tabler:alert-circle' />
                {t(
                  'Selecciona al menos una nave comparable para hacer el análisis.'
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* SECTION: Resultados */}
      {results.length === 0 && !isCalculating && (
        <EmptyState
          icon='tabler:chart-bar-off'
          title={t('Aún no hay resultados')}
          description={t(
            'Selecciona el incoterm, las naves comparables y ejecuta el cálculo.'
          )}
        />
      )}

      {results.length > 0 && (
        <>
          <SectionHeader
            eyebrow={t('paso 3')}
            title={t('Resultados del cálculo')}
            subtitle={t(
              'Perjuicio agregado por las 3 fuentes, y desglose por (variedad, calibre).'
            )}
            action={
              <div className='flex items-center gap-2 flex-wrap'>
                <Tooltip
                  content={
                    results.length === 0
                      ? t('Calcula el perjuicio primero para poder guardar.')
                      : eligibleSavableSources.length === 0
                        ? t(
                            'Ningún análisis califica — abre el diálogo para ver el motivo por cada uno.'
                          )
                        : ''
                  }
                  isDisabled={
                    results.length > 0 && eligibleSavableSources.length > 0
                  }
                  placement='bottom'
                >
                  <span>
                    <Button
                      color='primary'
                      startContent={<Icon icon='tabler:device-floppy' />}
                      isDisabled={results.length === 0}
                      onPress={() => {
                        // Pre-select only sources that qualify; modal explains
                        // per-source reasons even if nothing is eligible.
                        setSaveSelectedSources(eligibleSavableSources);
                        setSaveModalOpen(true);
                      }}
                    >
                      {t('Guardar Compensación')}
                    </Button>
                  </span>
                </Tooltip>
                <Popover placement='bottom-end'>
                  <PopoverTrigger>
                    <Button
                      variant='flat'
                      startContent={<Icon icon='tabler:file-type-pdf' />}
                      isDisabled={results.length === 0}
                    >
                      {t('Exportar PDF')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='p-4 w-72'>
                    <div className='w-full'>
                      <div className='text-sm font-semibold mb-2'>
                        {t('Idioma del PDF')}
                      </div>
                      <Tabs
                        selectedKey={pdfLanguage}
                        onSelectionChange={(k) =>
                          setPdfLanguage(k as 'es' | 'en')
                        }
                        size='sm'
                        fullWidth
                      >
                        <Tab key='en' title={t('English')} />
                        <Tab key='es' title={t('Español')} />
                      </Tabs>
                      <Divider className='my-3' />
                      <div className='text-sm font-semibold mb-2'>
                        {t('Análisis a incluir')}
                      </div>
                      <CheckboxGroup
                        value={pdfSources}
                        onValueChange={(v) =>
                          setPdfSources(v as SinisterSource[])
                        }
                      >
                        <Checkbox value='market'>{t('vs Mercado')}</Checkbox>
                        <Checkbox value='exporter'>
                          {t('vs Exportadora')}
                        </Checkbox>
                        <Checkbox value='industry'>
                          {t('vs Industria')}
                        </Checkbox>
                      </CheckboxGroup>
                      <Divider className='my-3' />
                      <Checkbox
                        isSelected={pdfIncludeDetail}
                        onValueChange={setPdfIncludeDetail}
                        size='sm'
                      >
                        <span className='text-sm'>
                          {t('Incluir desglose por línea')}
                        </span>
                      </Checkbox>
                      <div className='text-[11px] text-default-500 ml-6 mt-0.5'>
                        {t(
                          'Anexa una tabla con cada línea individual de la liquidación.'
                        )}
                      </div>
                      <Button
                        color='primary'
                        size='sm'
                        fullWidth
                        className='mt-3'
                        onPress={handleExportPdf}
                        isDisabled={pdfSources.length === 0}
                        startContent={<Icon icon='tabler:download' />}
                      >
                        {t('Generar PDF')}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            }
          />

          {/* KPI strip */}
          {(() => {
            const hasMarketCalc = summary.some(
              (r) => r.benchmarkMarket != null
            );
            const hasExporterCalc = summary.some(
              (r) => r.benchmarkExporter != null
            );
            const hasIndustryCalc = summary.some(
              (r) => r.benchmarkIndustry != null
            );
            const marketDisabledReason = !hasMarketCalc
              ? !marketRegion
                ? t('Falta seleccionar Mercado a comparar (SH o GZ).')
                : settlement.local_currency?.toUpperCase() !== 'RMB'
                  ? t('Sólo aplica si la liquidación está en RMB.')
                  : t(
                      'Sin datos de mercado para los productos en la fecha seleccionada.'
                    )
              : null;
            const exporterDisabledReason =
              !hasExporterCalc && selectedTripIds.size === 0
                ? t('Selecciona naves comparables para calcular.')
                : null;
            const industryDisabledReason =
              !hasIndustryCalc && selectedTripIds.size === 0
                ? t('Selecciona naves comparables para calcular.')
                : null;
            return (
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
                <KpiCard
                  label={t('Venta vs Mercado ({{currency}})', {
                    currency: marketDisplayCurrency,
                  })}
                  value={
                    hasMarketCalc
                      ? formatPnl(
                          convertMarketValue(summaryTotals.lossMarket),
                          marketDisplayCurrency,
                          0
                        )
                      : '—'
                  }
                  icon='tabler:world'
                  tone={
                    hasMarketCalc && summaryTotals.lossMarket < 0
                      ? 'danger'
                      : 'default'
                  }
                  hint={marketDisabledReason}
                />
                <KpiCard
                  label={t('{{mode}} vs Exportadora (USD)', {
                    mode: currencyMode,
                  })}
                  value={
                    hasExporterCalc
                      ? formatPnl(summaryTotals.lossExporter, 'USD', 0)
                      : '—'
                  }
                  icon='tabler:building-factory-2'
                  tone={
                    hasExporterCalc && summaryTotals.lossExporter < 0
                      ? 'danger'
                      : 'default'
                  }
                  hint={exporterDisabledReason}
                />
                <KpiCard
                  label={t('{{mode}} vs Industria (USD)', {
                    mode: currencyMode,
                  })}
                  value={
                    hasIndustryCalc
                      ? formatPnl(summaryTotals.lossIndustry, 'USD', 0)
                      : '—'
                  }
                  icon='tabler:chart-histogram'
                  tone={
                    hasIndustryCalc && summaryTotals.lossIndustry < 0
                      ? 'danger'
                      : 'default'
                  }
                  hint={industryDisabledReason}
                />
              </div>
            );
          })()}

          {/* Summary table — grouped headers */}
          <Card shadow='sm' className='mb-6'>
            <CardHeader className='flex items-start justify-between'>
              <div>
                <h2 className='text-base font-semibold flex items-center gap-2'>
                  <Icon icon='tabler:table' className='text-default-500' />
                  {t('Resumen por variedad y calibre')}
                </h2>
                <p className='text-xs text-default-500'>
                  {t(
                    'Una fila por combinación · valor de comparación y perjuicio para cada análisis.'
                  )}
                </p>
              </div>
              <Chip size='sm' variant='flat'>
                {t('{{count}} grupos', { count: summary.length })}
              </Chip>
            </CardHeader>
            <Divider />
            <CardBody className='p-0 overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='bg-default-100 dark:bg-default-50 text-default-600 text-xs'>
                    <th
                      rowSpan={2}
                      className='text-left font-semibold px-3 py-2 align-bottom border-b border-default-200 dark:border-default-100'
                    >
                      {t('Variedad')}
                    </th>
                    <th
                      rowSpan={2}
                      className='text-left font-semibold px-3 py-2 align-bottom border-b border-default-200 dark:border-default-100'
                    >
                      {t('Calibre')}
                    </th>
                    <th
                      rowSpan={2}
                      className='text-right font-semibold px-3 py-2 align-bottom border-b border-default-200 dark:border-default-100'
                    >
                      {t('Kilos')}
                    </th>
                    <th
                      rowSpan={2}
                      className='text-right font-semibold px-3 py-2 align-bottom border-b border-l border-default-200 dark:border-default-100 text-default-700 dark:text-default-300 whitespace-nowrap'
                    >
                      {t('Venta Propio')}
                      <div className='text-[10px] font-normal text-default-500'>
                        {t('({{currency}}) / kg', {
                          currency: marketDisplayCurrency,
                        })}
                      </div>
                    </th>
                    <th
                      rowSpan={2}
                      className='text-right font-semibold px-3 py-2 align-bottom border-b border-r border-default-200 dark:border-default-100 text-default-700 dark:text-default-300 whitespace-nowrap'
                    >
                      {t('{{mode}} Propio', { mode: currencyMode })}
                      <div className='text-[10px] font-normal text-default-500'>
                        {t('(USD) / kg')}
                      </div>
                    </th>
                    <th
                      colSpan={2}
                      className='text-center font-semibold px-3 py-2 border-b border-default-200 dark:border-default-100 text-blue-600 dark:text-blue-400'
                    >
                      {t('Venta vs Mercado ({{currency}})', {
                        currency: marketDisplayCurrency,
                      })}
                    </th>
                    <th
                      colSpan={2}
                      className='text-center font-semibold px-3 py-2 border-b border-l border-default-200 dark:border-default-100 text-emerald-600 dark:text-emerald-400'
                    >
                      {t('{{mode}} vs Exportadora (USD)', {
                        mode: currencyMode,
                      })}
                    </th>
                    <th
                      colSpan={2}
                      className='text-center font-semibold px-3 py-2 border-b border-l border-default-200 dark:border-default-100 text-violet-600 dark:text-violet-400'
                    >
                      {t('{{mode}} vs Industria (USD)', { mode: currencyMode })}
                    </th>
                  </tr>
                  <tr className='bg-default-50 dark:bg-default-100/30 text-default-500 text-[11px] uppercase tracking-wider'>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Comparación')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Perjuicio')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-l border-default-200 dark:border-default-100'>
                      {t('Comparación')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Perjuicio')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-l border-default-200 dark:border-default-100'>
                      {t('Comparación')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Perjuicio')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((r, idx) => (
                    <tr
                      key={`${r.variety}-${r.caliber}`}
                      className={`${
                        idx % 2 === 0
                          ? 'bg-content1'
                          : 'bg-default-50/40 dark:bg-default-50/20'
                      } hover:bg-default-100 dark:hover:bg-default-50/40 transition-colors`}
                    >
                      <td className='px-3 py-2 font-medium'>{r.variety}</td>
                      <td className='px-3 py-2'>{r.caliber}</td>
                      <td className='px-3 py-2 text-right tabular-nums'>
                        {Number(r.kilos || 0).toLocaleString()}
                      </td>
                      {/* Venta Propio (Mercado side — RMB or converted USD) */}
                      <td className='px-3 py-2 text-right tabular-nums border-l border-default-200 dark:border-default-100 text-default-700 dark:text-default-300'>
                        {(() => {
                          const v = convertMarketValue(r.actualMarketRmb);
                          if (v == null) return '-';
                          return marketDisplayCurrency === 'RMB'
                            ? formatRmb(v, 4)
                            : formatMoneyUSD(v, { decimals: 4 } as any);
                        })()}
                      </td>
                      {/* FOB/CIF Propio (USD) */}
                      <td className='px-3 py-2 text-right tabular-nums border-r border-default-200 dark:border-default-100 text-default-700 dark:text-default-300'>
                        {r.actualUsdPerKg != null
                          ? formatMoneyUSD(r.actualUsdPerKg, {
                              decimals: 4,
                            } as any)
                          : '-'}
                      </td>
                      {/* vs Mercado: Comparación | Perjuicio */}
                      <td className='px-3 py-2 text-right tabular-nums'>
                        {(() => {
                          const v = convertMarketValue(r.benchmarkMarket);
                          if (v == null) return '-';
                          return marketDisplayCurrency === 'RMB'
                            ? formatRmb(v, 4)
                            : formatMoneyUSD(v, { decimals: 4 } as any);
                        })()}
                      </td>
                      <td className='px-3 py-2 text-right'>
                        <LossValue
                          value={convertMarketValue(r.lossByMarket)}
                          formatter={(v) =>
                            marketDisplayCurrency === 'RMB'
                              ? formatRmb(v, 0)
                              : formatMoneyUSD(v, { decimals: 0 } as any)
                          }
                        />
                      </td>
                      {/* vs Exportadora */}
                      <td className='px-3 py-2 text-right tabular-nums border-l border-default-200 dark:border-default-100'>
                        {r.benchmarkExporter != null
                          ? formatMoneyUSD(r.benchmarkExporter, {
                              decimals: 4,
                            } as any)
                          : '-'}
                      </td>
                      <td className='px-3 py-2 text-right'>
                        <LossValue value={r.lossByExporter} />
                      </td>
                      {/* vs Industria */}
                      <td className='px-3 py-2 text-right tabular-nums border-l border-default-200 dark:border-default-100'>
                        {r.benchmarkIndustry != null
                          ? formatMoneyUSD(r.benchmarkIndustry, {
                              decimals: 4,
                            } as any)
                          : '-'}
                      </td>
                      <td className='px-3 py-2 text-right'>
                        <LossValue value={r.lossByIndustry} />
                      </td>
                    </tr>
                  ))}
                  <tr className='bg-default-100 dark:bg-default-50/40 font-semibold border-t-2 border-default-300 dark:border-default-100'>
                    <td className='px-3 py-2.5'>{t('TOTAL')}</td>
                    <td className='px-3 py-2.5'></td>
                    <td className='px-3 py-2.5 text-right tabular-nums'>
                      {Number(summaryTotals.kilos || 0).toLocaleString()}
                    </td>
                    <td className='px-3 py-2.5 border-l border-default-200 dark:border-default-100'></td>
                    <td className='px-3 py-2.5 border-r border-default-200 dark:border-default-100'></td>
                    <td className='px-3 py-2.5'></td>
                    <td className='px-3 py-2.5 text-right'>
                      <LossValue
                        value={convertMarketValue(summaryTotals.lossMarket)}
                        bold
                        formatter={(v) =>
                          marketDisplayCurrency === 'RMB'
                            ? formatRmb(v, 0)
                            : formatMoneyUSD(v, { decimals: 0 } as any)
                        }
                      />
                    </td>
                    <td className='px-3 py-2.5 border-l border-default-200 dark:border-default-100'></td>
                    <td className='px-3 py-2.5 text-right'>
                      <LossValue value={summaryTotals.lossExporter} bold />
                    </td>
                    <td className='px-3 py-2.5 border-l border-default-200 dark:border-default-100'></td>
                    <td className='px-3 py-2.5 text-right'>
                      <LossValue value={summaryTotals.lossIndustry} bold />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className='px-4 py-3 border-t border-default-200 dark:border-default-100 flex items-start gap-2 text-[11px] text-default-500'>
                <Icon
                  icon='tabler:info-circle'
                  className='shrink-0 mt-0.5 text-default-400'
                />
                <span>
                  {t(
                    'Los precios se muestran con 4 decimales. El perjuicio se calcula con precisión completa (sumas crudas) antes de redondear, por lo que multiplicar manualmente kilos × (V. Propio − Comparación) puede dar pequeñas diferencias por redondeo. El número de la columna Perjuicio es el correcto.'
                  )}
                </span>
              </div>
            </CardBody>
          </Card>

          {/* Per-line detail breakdown */}
          <Card shadow='sm' className='mb-6'>
            <CardHeader className='flex items-start justify-between gap-3'>
              <div>
                <h2 className='text-base font-semibold flex items-center gap-2'>
                  <Icon
                    icon='tabler:list-details'
                    className='text-default-500'
                  />
                  {t('Desglose por línea de detalle')}
                </h2>
                <p className='text-xs text-default-500'>
                  {t(
                    'Una fila por cada línea de la liquidación con su propio precio y perjuicio individual. Los benchmarks se heredan del grupo (variedad, calibre).'
                  )}
                </p>
              </div>
              <div className='flex items-center gap-2'>
                <Chip size='sm' variant='flat'>
                  {t('{{count}} líneas', { count: detailLines.length })}
                </Chip>
                <Button
                  size='sm'
                  variant='flat'
                  startContent={<Icon icon='tabler:file-spreadsheet' />}
                  onPress={handleExportDetailExcel}
                  isDisabled={detailLines.length === 0}
                >
                  {t('Exportar Excel')}
                </Button>
              </div>
            </CardHeader>
            <Divider />
            <CardBody className='p-0 overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='bg-default-100 dark:bg-default-50 text-default-600 text-xs'>
                    <th
                      rowSpan={2}
                      className='text-left font-semibold px-3 py-2 align-bottom border-b border-default-200 dark:border-default-100'
                    >
                      {t('Variedad')}
                    </th>
                    <th
                      rowSpan={2}
                      className='text-left font-semibold px-3 py-2 align-bottom border-b border-default-200 dark:border-default-100'
                    >
                      {t('Cal.')}
                    </th>

                    <th
                      rowSpan={2}
                      className='text-right font-semibold px-3 py-2 align-bottom border-b border-default-200 dark:border-default-100'
                    >
                      {t('Kilos')}
                    </th>
                    <th
                      rowSpan={2}
                      className='text-right font-semibold px-3 py-2 align-bottom border-b border-l border-default-200 dark:border-default-100 text-default-700 dark:text-default-300 whitespace-nowrap'
                    >
                      {t('Venta Propio')}
                      <div className='text-[10px] font-normal text-default-500'>
                        {t('({{currency}}) / kg', {
                          currency: marketDisplayCurrency,
                        })}
                      </div>
                    </th>
                    <th
                      rowSpan={2}
                      className='text-right font-semibold px-3 py-2 align-bottom border-b border-r border-default-200 dark:border-default-100 text-default-700 dark:text-default-300 whitespace-nowrap'
                    >
                      {t('{{mode}} Propio', { mode: currencyMode })}
                      <div className='text-[10px] font-normal text-default-500'>
                        {t('(USD) / kg')}
                      </div>
                    </th>
                    <th
                      colSpan={2}
                      className='text-center font-semibold px-3 py-2 border-b border-default-200 dark:border-default-100 text-blue-600 dark:text-blue-400'
                    >
                      {t('Venta vs Mercado ({{currency}})', {
                        currency: marketDisplayCurrency,
                      })}
                    </th>
                    <th
                      colSpan={2}
                      className='text-center font-semibold px-3 py-2 border-b border-l border-default-200 dark:border-default-100 text-emerald-600 dark:text-emerald-400'
                    >
                      {t('{{mode}} vs Exportadora (USD)', {
                        mode: currencyMode,
                      })}
                    </th>
                    <th
                      colSpan={2}
                      className='text-center font-semibold px-3 py-2 border-b border-l border-default-200 dark:border-default-100 text-violet-600 dark:text-violet-400'
                    >
                      {t('{{mode}} vs Industria (USD)', { mode: currencyMode })}
                    </th>
                  </tr>
                  <tr className='bg-default-50 dark:bg-default-100/30 text-default-500 text-[11px] uppercase tracking-wider'>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Comp.')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Perj.')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-l border-default-200 dark:border-default-100'>
                      {t('Comp.')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Perj.')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-l border-default-200 dark:border-default-100'>
                      {t('Comp.')}
                    </th>
                    <th className='text-right font-semibold px-3 py-1.5 border-b border-default-200 dark:border-default-100'>
                      {t('Perj.')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detailLines.map((d, idx) => {
                    const aM = convertMarketValue(d.actualMarketRmb);
                    const bM = convertMarketValue(d.benchmarkMarket);
                    const lM = convertMarketValue(d.lossMarketRmb);
                    const fmtM = (v: number | null, dec: 0 | 4) =>
                      v == null
                        ? '-'
                        : marketDisplayCurrency === 'RMB'
                          ? formatRmb(v, dec)
                          : formatMoneyUSD(v, { decimals: dec } as any);
                    return (
                      <tr
                        key={d.detail_id}
                        className={`${
                          idx % 2 === 0
                            ? 'bg-content1'
                            : 'bg-default-50/40 dark:bg-default-50/20'
                        } hover:bg-default-100 dark:hover:bg-default-50/40 transition-colors`}
                      >
                        <td className='px-3 py-2 font-medium'>{d.variety}</td>
                        <td className='px-3 py-2'>{d.caliber}</td>

                        <td className='px-3 py-2 text-right tabular-nums'>
                          {Number(d.kilos).toLocaleString()}
                        </td>
                        {/* Venta Propio (market side) */}
                        <td className='px-3 py-2 text-right tabular-nums border-l border-default-200 dark:border-default-100 text-default-700 dark:text-default-300'>
                          {fmtM(aM, 4)}
                        </td>
                        {/* FOB/CIF Propio (USD) */}
                        <td className='px-3 py-2 text-right tabular-nums border-r border-default-200 dark:border-default-100 text-default-700 dark:text-default-300'>
                          {formatMoneyUSD(d.actualUsdPerKg, {
                            decimals: 4,
                          } as any)}
                        </td>
                        {/* Venta vs Mercado */}
                        <td className='px-3 py-2 text-right tabular-nums'>
                          {fmtM(bM, 4)}
                        </td>
                        <td className='px-3 py-2 text-right'>
                          <LossValue
                            value={lM}
                            formatter={(v) =>
                              marketDisplayCurrency === 'RMB'
                                ? formatRmb(v, 0)
                                : formatMoneyUSD(v, { decimals: 0 } as any)
                            }
                          />
                        </td>
                        {/* Exportadora */}
                        <td className='px-3 py-2 text-right tabular-nums border-l border-default-200 dark:border-default-100'>
                          {d.benchmarkExporter != null
                            ? formatMoneyUSD(d.benchmarkExporter, {
                                decimals: 4,
                              } as any)
                            : '-'}
                        </td>
                        <td className='px-3 py-2 text-right'>
                          <LossValue value={d.lossExporterUsd} />
                        </td>
                        {/* Industria */}
                        <td className='px-3 py-2 text-right tabular-nums border-l border-default-200 dark:border-default-100'>
                          {d.benchmarkIndustry != null
                            ? formatMoneyUSD(d.benchmarkIndustry, {
                                decimals: 4,
                              } as any)
                            : '-'}
                        </td>
                        <td className='px-3 py-2 text-right'>
                          <LossValue value={d.lossIndustryUsd} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className='px-4 py-3 border-t border-default-200 dark:border-default-100 flex items-start gap-2 text-[11px] text-default-500'>
                <Icon
                  icon='tabler:info-circle'
                  className='shrink-0 mt-0.5 text-default-400'
                />
                <span>
                  {t(
                    'Cada línea es una línea de la liquidación original (no agregada). El benchmark de cada línea es el del grupo (variedad, calibre) al que pertenece.'
                  )}
                </span>
              </div>
            </CardBody>
          </Card>

          {/*
            Per-source detail tables — temporarily disabled because the resumen
            already shows benchmark + perjuicio per (variety, caliber) for all 3
            sources. Keeping the SourceSection component for future use.

            <SectionHeader
              title='Detalle por análisis'
              subtitle='Una tabla por fuente con el cálculo a nivel de variedad + calibre.'
            />
            <div className='space-y-4'>
              {(['market', 'exporter', 'industry'] as SinisterSource[]).map(
                (source) => (
                  <SourceSection
                    key={source}
                    source={source}
                    rows={bySource[source]}
                  />
                )
              )}
            </div>
          */}
        </>
      )}

      {/* Save-as-claim modal */}
      <Modal
        isOpen={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        size='lg'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <div className='flex items-center gap-2'>
              <Icon icon='tabler:device-floppy' className='text-success' />
              {t('Guardar Compensación')}
            </div>
            <div className='text-xs text-default-500 font-normal'>
              {t(
                'Sólo se pueden guardar análisis con resultado negativo (perjuicio real).'
              )}
            </div>
          </ModalHeader>
          <ModalBody>
            <div className='flex flex-col gap-3'>
              {eligibleSavableSources.length === 0 && (
                <Alert variant='warning' showIcon>
                  <div className='font-semibold mb-0.5'>
                    {t('No hay análisis que se puedan guardar en este momento.')}
                  </div>
                  <div className='text-xs font-normal'>
                    {t(
                      'Cada uno tiene su motivo específico abajo (resultado positivo, sin datos, o ya distribuido). Resuelve el bloqueo o recalcula con otros parámetros.'
                    )}
                  </div>
                </Alert>
              )}
              <div className='text-sm font-semibold'>
                {t('¿Qué análisis quieres guardar?')}
              </div>
              {(['market', 'exporter', 'industry'] as SinisterSource[]).map(
                (source) => {
                  const meta = SOURCE_META[source];
                  const totalKey =
                    source === 'market'
                      ? summaryTotals.lossMarket
                      : source === 'exporter'
                        ? summaryTotals.lossExporter
                        : summaryTotals.lossIndustry;
                  const detailsCount = detailLines.filter((d) =>
                    source === 'market'
                      ? d.benchmarkMarket != null
                      : source === 'exporter'
                        ? d.benchmarkExporter != null
                        : d.benchmarkIndustry != null
                  ).length;
                  const slot: keyof SinisterClaimPresence =
                    source === 'market'
                      ? 'market'
                      : source === 'exporter'
                        ? 'exporter'
                        : 'industry';
                  const existing = savedClaims?.[slot];
                  const checked = saveSelectedSources.includes(source);
                  const eligibility = sourceCanBeSaved(source);
                  const disabled = !eligibility.allowed;
                  const currency =
                    source === 'market' ? marketDisplayCurrency : 'USD';
                  const valueRaw =
                    source === 'market'
                      ? convertMarketValue(totalKey)
                      : totalKey;
                  // Claim amount = positive perjuicio (negate the P&L)
                  const claimAmountDisplay =
                    valueRaw != null && valueRaw < 0
                      ? Math.abs(valueRaw)
                      : null;
                  return (
                    <div
                      key={source}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        checked && !disabled
                          ? `${meta.ring} ${meta.tint}`
                          : 'border-default-200 dark:border-default-100 bg-content1'
                      } ${disabled ? 'opacity-50' : ''}`}
                    >
                      <Checkbox
                        isSelected={checked}
                        isDisabled={disabled}
                        onValueChange={(v) => {
                          setSaveSelectedSources((prev) =>
                            v
                              ? Array.from(new Set([...prev, source]))
                              : prev.filter((s) => s !== source)
                          );
                        }}
                      />
                      <div className='flex-1'>
                        <div className='flex items-center gap-2'>
                          <Icon icon={meta.icon} className={meta.accent} />
                          <span className={`font-semibold ${meta.accent}`}>
                            {meta.title}
                          </span>
                          {existing && (
                            <Chip
                              size='sm'
                              variant='flat'
                              color={
                                existing.status === 'distributed'
                                  ? 'danger'
                                  : 'warning'
                              }
                              startContent={
                                <Icon
                                  icon={
                                    existing.status === 'distributed'
                                      ? 'tabler:lock'
                                      : 'tabler:bookmark-filled'
                                  }
                                  className='ml-1 text-xs'
                                />
                              }
                            >
                              {t('Ya guardado ({{status}})', {
                                status: existing.status,
                              })}
                            </Chip>
                          )}
                        </div>
                        <div className='text-xs text-default-500 mt-0.5'>
                          {t('{{count}} líneas con benchmark · P&L:', {
                            count: detailsCount,
                          })}{' '}
                          <span className='font-semibold text-foreground'>
                            {valueRaw != null
                              ? formatPnl(valueRaw, currency, 0)
                              : '—'}
                          </span>
                          {claimAmountDisplay != null && (
                            <span className='ml-2'>
                              · {t('Monto a reclamar:')}{' '}
                              <span className='font-semibold text-danger'>
                                {currency === 'RMB'
                                  ? formatRmb(claimAmountDisplay, 0)
                                  : formatMoneyUSD(claimAmountDisplay, {
                                      decimals: 0,
                                    } as any)}
                              </span>
                            </span>
                          )}
                        </div>
                        {disabled && (
                          <div className='text-xs text-danger-600 dark:text-danger-400 mt-1 flex items-start gap-1'>
                            <Icon
                              icon='tabler:alert-circle'
                              className='shrink-0 mt-0.5'
                            />
                            <span>{eligibility.reason}</span>
                          </div>
                        )}
                        {!disabled &&
                          existing &&
                          existing.status !== 'distributed' && (
                            <div className='text-xs text-warning-600 dark:text-warning-400 mt-1 flex items-start gap-1'>
                              <Icon
                                icon='tabler:alert-triangle'
                                className='shrink-0 mt-0.5'
                              />
                              <span>
                                {t('Existe un claim previo en estado')}{' '}
                                <strong>{existing.status}</strong> (#
                                {existing.id})
                                {t(
                                  '. Al guardar lo cancelaremos automáticamente.'
                                )}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                }
              )}
              <Textarea
                label={t('Notas (opcional)')}
                labelPlacement='outside'
                placeholder={t('Contexto, justificación, etc.')}
                value={saveNotes}
                onValueChange={setSaveNotes}
                minRows={2}
                maxRows={5}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => setSaveModalOpen(false)}
              isDisabled={isSaving}
            >
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              onPress={handleSaveClaims}
              isLoading={isSaving}
              isDisabled={saveSelectedSources.length === 0}
              startContent={<Icon icon='tabler:device-floppy' />}
            >
              {t('Guardar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <SettlementHistoryDrawer
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        settlement={settlement}
        onChange={reloadHistoryCount}
      />
    </PageLayout>
  );
}

function BitacoraButton({
  count,
  label,
  onPress,
}: {
  count: number;
  label: string;
  onPress: () => void;
}) {
  return (
    <Tooltip content={label}>
      <div className='relative'>
        <Button
          isIconOnly
          size='sm'
          variant='flat'
          aria-label={label}
          onPress={onPress}
        >
          <Icon icon='tabler:book' className='text-default-600' />
        </Button>
        {count > 0 && (
          <span className='absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white shadow-sm'>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </div>
    </Tooltip>
  );
}

// ─── Helper components ───

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className='mb-3 flex items-center justify-between'>
      <div className='mb-3'>
        {eyebrow && (
          <div className='text-[11px] uppercase tracking-widest font-semibold text-primary/80 mb-0.5'>
            {eyebrow}
          </div>
        )}
        <h2 className='text-lg font-bold text-foreground'>{title}</h2>
        {subtitle && (
          <p className='text-xs text-default-500 mt-0.5'>{subtitle}</p>
        )}
      </div>
      {action && <div className='mt-2'>{action}</div>}
    </div>
  );
}

function MetaCell({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className='p-4 sm:p-5 flex items-start gap-3'>
      <Icon icon={icon} className='text-lg text-default-400 mt-0.5' />
      <div className='min-w-0'>
        <div className='text-[10px] uppercase tracking-wider text-default-500 font-semibold'>
          {label}
        </div>
        <div className='text-sm font-semibold text-foreground truncate'>
          {value}
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
  tone,
  prominent,
  hint,
}: {
  label: string;
  value: string;
  icon: string;
  tone: 'danger' | 'default';
  prominent?: boolean;
  /** Optional explanation when value is empty/dash, shown below the value. */
  hint?: string | null;
}) {
  const isEmpty = value === '—' || value === '-';
  const valueClass =
    tone === 'danger'
      ? 'text-danger'
      : isEmpty
        ? 'text-default-400'
        : prominent
          ? 'text-foreground'
          : 'text-foreground';
  return (
    <Card shadow='sm'>
      <CardBody className='p-4'>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] uppercase tracking-wider font-semibold text-default-500'>
            {label}
          </div>
          <Icon
            icon={icon}
            className={`text-lg ${
              tone === 'danger' ? 'text-danger' : 'text-default-400'
            }`}
          />
        </div>
        <div className={`text-2xl font-bold mt-1 tabular-nums ${valueClass}`}>
          {value}
        </div>
        {hint && (
          <div className='text-[11px] text-warning-600 dark:text-warning-400 mt-1.5 flex items-start gap-1'>
            <Icon
              icon='tabler:alert-circle'
              className='shrink-0 mt-0.5 text-xs'
            />
            <span>{hint}</span>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LossValue({
  value,
  bold,
  formatter,
}: {
  value: number | null;
  bold?: boolean;
  formatter?: (v: number) => string;
}) {
  if (value == null) return <span className='text-default-400'>-</span>;
  // Sign convention: positive = gain (green), negative = loss (red).
  const cls =
    value < 0 ? 'text-danger' : value > 0 ? 'text-success' : 'text-default-500';
  const fmt =
    formatter ?? ((v: number) => formatMoneyUSD(v, { decimals: 0 } as any));
  // For positive values prepend an explicit "+" so the sign is unambiguous.
  // Negative values already render with a leading "-" from the formatter.
  const formatted = value > 0 ? `+${fmt(value)}` : fmt(value);
  return (
    <span
      className={`tabular-nums ${cls} ${bold ? 'font-bold' : 'font-medium'}`}
    >
      {formatted}
    </span>
  );
}

function SourceSection({
  source,
  rows,
  meta,
}: {
  source: SinisterSource;
  rows: SinisterLossResult[];
  meta: SourceMeta;
}) {
  const { t } = useTranslation('common');
  const totalLoss = rows.reduce((acc, r) => acc + (Number(r.loss_usd) || 0), 0);

  if (rows.length === 0) return null;

  return (
    <Card shadow='sm' className='overflow-hidden'>
      <div className={`${meta.tint}`}>
        <CardHeader className='flex items-start justify-between gap-3 p-5'>
          <div className='flex items-center gap-3'>
            <div className='rounded-medium border border-default-200 p-2.5'>
              <Icon icon={meta.icon} className={`text-2xl ${meta.accent}`} />
            </div>
            <div>
              <h2 className={`text-base font-semibold ${meta.accent}`}>
                {meta.title}
              </h2>
              <p className='text-xs text-default-500'>{meta.short}</p>
            </div>
          </div>
          <div className='text-right'>
            <div className='text-[10px] uppercase tracking-wider text-default-500 font-semibold'>
              {t('Perjuicio')}
            </div>
            <div
              className={`text-2xl font-bold tabular-nums ${
                totalLoss > 0
                  ? 'text-danger'
                  : totalLoss < 0
                    ? 'text-success'
                    : 'text-foreground'
              }`}
            >
              {formatMoneyUSD(totalLoss)}
            </div>
          </div>
        </CardHeader>
      </div>
      <Divider />
      <CardBody className='p-0'>
        <Table
          aria-label={`${source}-results`}
          removeWrapper
          classNames={{
            th: 'bg-default-100 dark:bg-default-50 text-default-600 text-xs',
          }}
        >
          <TableHeader>
            <TableColumn>{t('Variedad')}</TableColumn>
            <TableColumn>{t('Calibre')}</TableColumn>
            <TableColumn>{t('Kilos')}</TableColumn>
            <TableColumn>Actual $/kg</TableColumn>
            <TableColumn>Benchmark $/kg</TableColumn>
            <TableColumn>N</TableColumn>
            <TableColumn>{t('Perjuicio')} USD</TableColumn>
          </TableHeader>
          <TableBody>
            {rows.map((r, idx) => {
              const insufficient = r.benchmark_price_per_kg == null;
              return (
                <TableRow key={`${r.variety}-${r.caliber}-${idx}`}>
                  <TableCell className='font-medium'>
                    {r.variety || '-'}
                  </TableCell>
                  <TableCell>{r.caliber || '-'}</TableCell>
                  <TableCell className='tabular-nums'>
                    {Number(r.kilos || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className='tabular-nums'>
                    {r.actual_price_per_kg != null
                      ? Number(r.actual_price_per_kg).toFixed(2)
                      : '-'}
                  </TableCell>
                  <TableCell className='tabular-nums'>
                    {insufficient ? (
                      <Chip size='sm' variant='flat' color='default'>
                        {t('Sin datos')}
                      </Chip>
                    ) : (
                      Number(r.benchmark_price_per_kg).toFixed(2)
                    )}
                  </TableCell>
                  <TableCell className='tabular-nums text-default-500'>
                    {r.data_points}
                  </TableCell>
                  <TableCell>
                    <LossValue value={r.loss_usd} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}
